import { Fragment, lazy, Suspense, useDeferredValue, useEffect, useMemo, useRef,
         useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  ListSubheader,
  IconButton,
  Tooltip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
  ACTIVE_ATTENDANCE_STATUSES,
  INCIDENT_SCOPES,
  INCIDENT_SCOPE_ALL,
  INCIDENT_SCOPE_ALLOCATED,
  INCIDENT_SCOPE_INTERACTED,
  INCIDENT_TYPES,
  WRAP_UP_FILTERS,
  WRAP_UP_NONE,
  WRAP_UP_PHOTO_CONSENT,
  WRAP_UP_FOLLOWUP_CONSENT,
  WRAP_UP_RACE_ORGANISER,
  PATIENT_SEXES,
  LOCATION_TYPES,
  INCIDENT_DETAIL_TYPES,
  INCIDENT_DETAIL_TYPE_INFO,
  INCIDENT_SUBTYPES,
  OTHER_SUBTYPE,
  detailSubtypeLabel,
  RESPONSE_TEAM_DEPLOYMENTS,
  MOUNTAIN_SAFETY_DEPLOYMENTS,
  DISPOSAL_TRANSPORTS,
  DISPOSAL_DESTINATIONS,
  ADVISED_TO_RETIRE_OPTIONS,
  PATIENT_CONSENT_INFO,
  consentNeedsDetails,
  isChildPatient,
  INCIDENT_STATUSES,
  MEDICAL_INCIDENT_TYPES,
  needsClinicalReview,
  SAFETY_NETTING,
  SAFETY_NETTING_OTHER,
  isOtherNetting,
  normaliseNetting,
  otherNettingText,
  otherNettingValue,
  INCIDENT_STATUS_COLOR,
  INCIDENT_DETAIL_PROMPTS,
  ATTENDANCE_GRACE_DAYS,
  STICKY_LOCATION_ROLES,
  CLINICAL_ATTENDANCE_ROLES,
  DEFAULT_CLINICAL_INCIDENT_TYPE,
  sortByLocationType,
  sortByAttendanceRole,
  surnameKey,
} from "../constants";
import { exportIncidents } from "../exportExcel";
import { exportIncidentPdf, exportIncidentsPdf } from "../exportPdf";
import { exportIncidentPhotos } from "../exportPhotos";
import { fileSlug } from "../download";
import { fmtDateTime, allocatedLabel, patientLabel } from "../incidentLabels";
import { incidentLink } from "../incidentLink";
import { autoReportsOnSave } from "../clinicalReportRules";
import { API_BASE_URL } from "../config";
import { useCurrentUser } from "../useCurrentUser";
import { useMyPersonnel } from "../useMyPersonnel";
import { useMe } from "../MeContext";
import { combineLatLng, parseLatLng, normalizeLatLng, w3wUrl, mapsUrl, gridRefUrl,
         convertAdornment } from "../geo";
import { useLocationConvert } from "../useLocationConvert";
import { notify } from "../notify";
import { drugLine, standingDrugs } from "../incidentNotes";
import {
  creatorOptions, allocationOptions, createdByMatches, allocatedToMatches,
  choiceMatches, CHOICE_ANY, CHOICE_NONE,
  subtypeOptions as subtypeFilterOptions,  // the form has its own subtypeOptions
  patientMatches, recordText, searchTerms, recordMatches,
} from "../incidentFilters";
import { trackingUrl, openTracking } from "../tracking";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import AuditTrail from "../components/AuditTrail";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClearIcon from "@mui/icons-material/Clear";
import MedicationIcon from "@mui/icons-material/Medication";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import PlaceIcon from "@mui/icons-material/Place";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";
import ShareIcon from "@mui/icons-material/Share";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { RichTextEditor, RichTextView } from "../components/RichText";
import { imageUrls } from "../richText";
import ReminderFormDialog from "../components/ReminderFormDialog";
import RemindersDialog from "../components/RemindersDialog";
import GiveDrugDialog from "../components/GiveDrugDialog";
import RaiseClinicalReportDialog from "../components/RaiseClinicalReportDialog";
import ClinicalReportDialog from "../components/ClinicalReportDialog";
import DrugAdministrationList from "../components/DrugAdministrationList";
import IncidentTargetDialog from "../components/IncidentTargetDialog";
import IncidentVisibilityNote from "../components/IncidentVisibilityNote";
import ParticipantDetailsDialog, { WatchListAlert } from "../components/ParticipantDetails";
import PatientIncidentHistory from "../components/PatientIncidentHistory";

const INCIDENTS_API = `${API_BASE_URL}/incidents`;
const EVENTS_API = `${API_BASE_URL}/events`;
const ATT_API = `${API_BASE_URL}/attendances`;
const PARTICIPANTS_API = `${API_BASE_URL}/participants`;
const LOCATIONS_API = `${API_BASE_URL}/locations`;
const ROUTES_API = `${API_BASE_URL}/event-routes`;
const TEAMS_API = `${API_BASE_URL}/event-teams`;
const RACES_API = `${API_BASE_URL}/event-races`;
const NOTES_API = `${API_BASE_URL}/incident-notes`;
const REMINDERS_API = `${API_BASE_URL}/reminders`;

// Sticky defaults live in the browser (not the DB): the last event chosen when
// filtering/creating, and the pre-defined location picked on the last incident
// created (kept with its event, since locations are event-specific). The location
// records the *last* incident and nothing older: filing one without a pre-defined
// location clears it, rather than leaving the one before that to be offered again.
const LS_LAST_EVENT = "incidents.lastEventId";
const LS_LAST_STATUS = "incidents.lastStatus"; // "" (All), "Ongoing", "Closed", …
const LS_LAST_LOCATION = "incidents.lastLocation"; // JSON: { eventId, locationId }
// Which way the event's incidents are being read, list or map. In the browser and
// deliberately not in the URL: the ?open= effect below clears every search param
// on mount, so a view choice put there would be wiped the moment somebody
// followed a texted link in.
const LS_VIEW = "incidents.view";
const lsGet = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* ignore */ } };
const lsDel = (k) => { try { window.localStorage.removeItem(k); } catch { /* ignore */ } };

// The attended event nearest to today: an ongoing event (distance 0), otherwise
// whichever is fewest days away by its start (future) or end (past) date.
const closestAttendedEvent = (evts) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let best = null, bestDist = Infinity;
  for (const e of evts) {
    if (!e.start_date) continue;
    const start = new Date(e.start_date).getTime();
    const end = e.end_date ? new Date(e.end_date).getTime() : start;
    const dist = today < start ? start - today : today > end ? today - end : 0;
    if (dist < bestDist) { bestDist = dist; best = e; }
  }
  return best;
};

// Today minus `days`, as "YYYY-MM-DD" for comparison with the event date columns.
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Whether an event is still within reach of an attendance-derived permission:
// upcoming, running, or finished within the grace period. Undated events count as
// current. The backend enforces the same cut-off (authz.ATTENDANCE_GRACE_DAYS), so
// this only keeps unreachable events out of the picker.
const withinAccessWindow = (e) => {
  const end = e?.end_date || e?.start_date || "";
  return !end || end >= isoDaysAgo(ATTENDANCE_GRACE_DAYS);
};

// Whether giving a drug on this event's incident comes off the inventory. Before
// the event starts it doesn't: the days beforehand are when the app is set up and
// tried out, and those practice incidents mustn't empty the bags being packed.
// The backend decides (deducts_stock in core/incident_drugs.py); this only words
// the dialog to match. Undated events count as running.
const eventDeductsStock = (e) => !e?.start_date || e.start_date <= isoDaysAgo(0);

const BLANK = {
  event_id: "",
  patient_name: "",
  patient_race_number: "",
  patient_age: "",
  patient_sex: "",
  patient_race: "",
  patient_phone: "",
  patient_email: "",
  patient_participant_id: null,
  incident_type: "",
  incident_summary: "",
  location_type: "",
  location_details: "",
  location_what3words: "",
  location_grid_ref: "",
  location_postcode: "",
  lat_long: "",
  detail_type: "",
  detail_subtype: "",
  detail_other: "",
  incident_details: "",
  response_team_deployment: "",
  mountain_safety_deployment: "",
  disposal_transport: "",
  disposal_destination: "",
  with_whom: "",
  phone_status: "",
  alternative_phones: "",
  experience: "",
  weather: "",
  welfare: "",
  patient_consent: "",  // a PATIENT_CONSENT_OPTIONS entry; "" = not recorded
  patient_consent_details: "",
  patient_consent_child_by: "",
  treatment_given: "",
  advised_to_retire: "",  // an ADVISED_TO_RETIRE_OPTIONS entry; "" = not advised
  safety_netting: [],  // names from SAFETY_NETTING; [] = none recorded
  // Both null = allocated to the creator; at most one of them is ever set.
  allocated_to_personnel_id: null,
  allocated_to_team_id: null,
  consent_teaching_images: false,
  consent_followup: false,
  status: "Ongoing",
  clinical_review: false,
};

// Safety netting held in SAFETY_NETTING's own order, whatever order it arrived or
// was ticked in. Two reasons: the record then reads in the same order as the
// checkboxes wherever it's shown or exported, and the unsaved-changes check (a
// stringify of the whole form) doesn't fire on a tick and untick. A name that is
// no longer an option — one renamed since the incident was written — is kept, on
// the end: it is what the clinician recorded, and dropping it would edit the
// record by opening it.
// Other goes last of all, where its checkbox is — it is the catch-all, and it reads
// as one at the end of a list rather than in the middle of the named categories.
const orderedNetting = (names) => {
  const chosen = new Set(names);
  const known = SAFETY_NETTING.filter(o => chosen.has(o.name)).map(o => o.name);
  const rest = names.filter(n => !SAFETY_NETTING.some(o => o.name === n));
  return [...known, ...rest.filter(n => !isOtherNetting(n)), ...rest.filter(isOtherNetting)];
};

// What was deployed in response, and how and where the patient left:
// [field, label, options]. Recorded for *every* incident, not only on-course ones
// (PROMPT_FIELDS below), but set on few of them — so they sit in the Other
// section near the end of the form, always editable like Status, rather than
// taking room at the top of Incident details. The same list drives the More
// filters panel's selects.
const DEPLOYMENT_FIELDS = [
  ["response_team_deployment", "Response Team deployment", RESPONSE_TEAM_DEPLOYMENTS],
  ["mountain_safety_deployment", "Mountain Safety deployment", MOUNTAIN_SAFETY_DEPLOYMENTS],
  ["disposal_transport", "Disposal transport", DISPOSAL_TRANSPORTS],
  ["disposal_destination", "Disposal destination", DISPOSAL_DESTINATIONS],
];

// Free-text on-course fields, each edited with its own prompting helper text
// (INCIDENT_DETAIL_PROMPTS in constants.js).
const PROMPT_FIELDS = [
  ["with_whom", "With whom"],
  ["phone_status", "Phone status"],
  ["alternative_phones", "Alternative phones"],
  ["experience", "Experience"],
  ["weather", "Weather"],
  ["welfare", "Welfare"],
];

// One person's option in the new incident's allocation picker. A single line: whether
// they can be texted is said once, over the group they sit in. Mirrors personItem in
// components/IncidentTargetDialog.jsx, which offers the same people.
const allocItem = (o) => (
  <MenuItem key={`p:${o.id}`} value={`p:${o.id}`}>
    {o.name}{o.role ? ` — ${o.role}` : ""}
  </MenuItem>
);

// Why the photo controls are disabled on an incident that hasn't been saved: a
// photo is stored against the incident it belongs to, so there has to be one.
// Points at "Save & continue" rather than at Save, since that files the incident
// without closing the dialog you'd be coming back to.
const PHOTO_HINT = "Press Save & continue to file the incident, then add a photo.";
// A clinical photograph shouldn't be left on a responder's phone, so a medical
// incident takes photos only from the in-app camera, which saves nothing to the
// device, or from the clipboard. Files can't be picked or dropped.
const CLINICAL_FILES_HINT =
  "Photos on a medical incident are taken with the camera button, so no copy is left on the device.";

const VIEW_LIST = "list";
const VIEW_MAP = "map";

// Leaflet and its stylesheet only matter to the one view, so the map is its own
// chunk — the trade qrcode and jszip already take, and the app's first use of
// React.lazy. At module scope, not inside the component, or the chunk would be
// re-requested on every render.
const IncidentMap = lazy(() => import("../components/IncidentMap"));

// Phone Finder request status -> MUI Chip colour + human label.
const PHONE_FIND_STATUS = {
  pending: { color: "warning", label: "Awaiting response" },
  located: { color: "success", label: "Located" },
  error:   { color: "error", label: "Device error" },
  expired: { color: "default", label: "Link expired" },
};

export default function Incidents({ userMode = false }) {
  const me = useCurrentUser();
  // `myRoles` is the /me payload, read here only for the caller's attendance role
  // per event (which decides whether the location pre-fill applies — see
  // stickyLocationRole); `me` above is their name/email for "created by".
  const { can, me: myRoles } = useMe();
  // The post-event wrap-up group — the three filters and the two exports beside
  // them. One permission for the lot (Super Admin only, see the matrix), and it
  // isn't event-scoped: nobody holds it through an attendance.
  const canWrapUp = can("Incidents.wrapUp");
  const fullScreen = useFullScreenDialog();
  // The signed-in user's Personnel record and the events they attend. In user
  // view these are the only selectable events (filter + new incidents); in admin
  // view they're still resolved so the event filter can default to the user's
  // nearest attended event.
  const { person: myPerson, loading: myPersonLoading } = useMyPersonnel();
  const [myEvents, setMyEvents] = useState([]);
  const [myEventsReady, setMyEventsReady] = useState(false); // attended events resolved (or none)
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true); // true until the first incidents fetch resolves
  const [events, setEvents] = useState([]);
  const [attendances, setAttendances] = useState([]); // attendances for the form's selected event
  const [teams, setTeams] = useState([]); // the form's event's teams, offered for allocation
  const [races, setRaces] = useState([]); // the form's event's races + tracking link prefixes
  const [myTeamIds, setMyTeamIds] = useState(new Set()); // teams the signed-in user is in, any event
  const [participants, setParticipants] = useState([]); // entry list for the form's selected event
  const [viewParticipant, setViewParticipant] = useState(null); // participant shown in the read-only dialog
  const [withWhomSearch, setWithWhomSearch] = useState(""); // text in the "add an entrant to With whom" picker
  const [locations, setLocations] = useState([]); // pre-defined locations for the form's selected event
  const [pickedLocation, setPickedLocation] = useState(null); // location chosen in the picker (display only)
  const [form, setForm] = useState(BLANK);
  // The four location fields are inter-convertible; this shared hook fills the
  // other three (or all four, from a Grid Reference Finder link) preserving precision.
  const {
    busy: populateBusy, grUrl, setGrUrl,
    fromW3w, fromGridRef, fromLatLng, fromPostcode, fromGrUrl: populateFromGrUrl,
    fromDevice,
  } = useLocationConvert({
    setForm,
    keys: { w3w: "location_what3words", gr: "location_grid_ref", pc: "location_postcode", ll: "lat_long" },
  });
  const [initialForm, setInitialForm] = useState(BLANK); // form snapshot at open, to detect unsaved changes
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [current, setCurrent] = useState(null); // the incident being edited (for read-only created_* fields)
  const [auditOpen, setAuditOpen] = useState(false); // incident history popup
  const [notes, setNotes] = useState([]); // notes on the incident being edited
  // Drugs given on the incident being edited (models/drug_administration.py),
  // reloaded with the notes — see refreshNotes.
  const [drugs, setDrugs] = useState([]);
  const [newNote, setNewNote] = useState(""); // draft note text
  const [patientHistory, setPatientHistory] = useState([]); // every incident for the form's patient
  // What was open before the reader followed a patient-history row to another
  // incident, held so closing that one puts them back in it — see followHistory.
  // Null the rest of the time, which is how close() knows there's nothing to
  // return to.
  const [held, setHeld] = useState(null);
  const [confirmKeepHeld, setConfirmKeepHeld] = useState(false);
  // Whether they actually added to the incident they went to, which is what makes
  // the held draft worth a question instead of a silent restore. A ref, not state:
  // saving the followed incident marks this and closes in the same tick, and a
  // state update wouldn't be visible to the close() that follows it.
  const wroteToFollowed = useRef(false);
  const [incidentReminder, setIncidentReminder] = useState(null); // the user's reminder for the open incident (or null)
  const [reminderFormOpen, setReminderFormOpen] = useState(false); // set/edit reminder for the open incident
  const [confirmDeleteReminder, setConfirmDeleteReminder] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false); // "My reminders" list dialog
  const [drugOpen, setDrugOpen] = useState(false); // record a drug given from stock
  const [reportOpen, setReportOpen] = useState(false); // raise a clinical report
  // The clinical reports naming the open incident — only fetched for a reader who
  // may open them (ClinicalReports.view) — and the one opened over the incident.
  const [linkedReports, setLinkedReports] = useState([]);
  const [reportsKey, setReportsKey] = useState(0);  // bumped to refetch them
  const [openReportId, setOpenReportId] = useState(null);
  // Pointing the incident at somebody: null, or "share" / "allocate" for the
  // dialog the two share (components/IncidentTargetDialog.jsx). Both apply straight
  // away rather than waiting for the incident's own Save, since each is an act with
  // an SMS attached and not a field being edited.
  const [targetAction, setTargetAction] = useState(null);
  const [phoneFinds, setPhoneFinds] = useState([]); // phone-find requests for the open incident
  const [phoneFindOpen, setPhoneFindOpen] = useState(false); // enter-number dialog
  const [phoneFindPhone, setPhoneFindPhone] = useState(""); // number typed in that dialog
  const [phoneFindBusy, setPhoneFindBusy] = useState(false); // send in flight
  const [phoneFindLink, setPhoneFindLink] = useState(null); // link from the last request (for copy/test)
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventFilter, setEventFilter] = useState(
    searchParams.get("event") ? Number(searchParams.get("event")) : "");
  // Status filter defaults to the last value used (incl. "All"), from localStorage.
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = lsGet(LS_LAST_STATUS);
    return s != null && (s === "" || INCIDENT_STATUSES.includes(s)) ? s : "";
  });
  const [textFilter, setTextFilter] = useState("");
  // One incident type, or "" for all. Not remembered between visits, unlike the
  // status filter: a status is how somebody likes to work, a type is a question
  // asked once. Offered only to the roles that see the whole event (canViewAll).
  const [typeFilter, setTypeFilter] = useState("");
  const [pendingReview, setPendingReview] = useState(false); // show only incidents awaiting clinical review
  // "More filters" — the ones mainline use doesn't need, kept in a panel that is
  // closed by default (and unmounted while closed). None is remembered between
  // visits: a hidden filter that survives a reload is how an incident "vanishes".
  // Whatever is set shows as a chip under the bar while the panel is shut, for the
  // same reason. Rules in incidentFilters.js.
  const [moreOpen, setMoreOpen] = useState(false);
  const [createdBy, setCreatedBy] = useState(null);     // a creatorOptions() entry
  const [allocatedTo, setAllocatedTo] = useState(null); // an allocationOptions() entry
  const [deployFilter, setDeployFilter] = useState({});  // DEPLOYMENT_FIELDS key -> choice
  const [detailTypeFilter, setDetailTypeFilter] = useState("");
  const [subtypeFilter, setSubtypeFilter] = useState("");
  const [recordFilter, setRecordFilter] = useState("");  // words in the record
  // How much of what the reader may see to actually list (INCIDENT_SCOPES). The
  // User view opens on their own incidents, the Admin view on everything — which is
  // what the old "Just my incidents" tick defaulted to in each.
  const [scope, setScope] = useState(
    userMode ? INCIDENT_SCOPE_INTERACTED : INCIDENT_SCOPE_ALL);
  const [exportMenu, setExportMenu] = useState(null); // anchor for the export-scope menu (Super Admin)
  const [exportingAll, setExportingAll] = useState(false); // the every-event export is a fetch, not just a write
  // Post-event wrap-up (Incidents.wrapUp, Super Admin only): which of its filters
  // is applied, the menu behind its export button, and whether one of those
  // exports is in flight — both fetch or fetch-and-zip before anything downloads.
  const [wrapUp, setWrapUp] = useState(WRAP_UP_NONE);
  const [wrapUpMenu, setWrapUpMenu] = useState(null);
  const [wrapUpBusy, setWrapUpBusy] = useState(false);
  // List or map (LS_VIEW). `view` is only what was asked for — `mapView` below is
  // what's rendered, once the permission has had its say.
  const [view, setView] = useState(() => (lsGet(LS_VIEW) === VIEW_MAP ? VIEW_MAP : VIEW_LIST));
  // The event's pre-defined locations, for the map's pins. The dialog fetches the
  // same list (further down) but keys it on the *form's* event, which is unset
  // while somebody is only browsing — so the map needs its own.
  const [mapLocations, setMapLocations] = useState([]);
  const [mapLocationsFailed, setMapLocationsFailed] = useState(false);
  // The event's course routes (GPX), drawn on the map, and whether the map's
  // extra layers have arrived — its one-off fit waits for them.
  const [mapRoutes, setMapRoutes] = useState([]);
  const [mapExtrasLoaded, setMapExtrasLoaded] = useState(false);
  // The ids of the listed event's Race-Organiser incidents, from the server —
  // null while unknown, which is not the same as "none" (see wrapUpOk). Only
  // fetched when that filter is actually selected.
  const [raceOrganiserIds, setRaceOrganiserIds] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false); // GDPR data-erasure prompt
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  // One write at a time from the dialog: Save and Add Note are the two buttons
  // that post, and a double-tap filed a second incident or a second copy of the
  // same note (see useWriteLock).
  const { busy: saving, run: runWrite, release: releaseWrite } = useWriteLock();
  // Most sections rarely change after creation, so on an existing incident they
  // show read-only and compact until the section's Edit button is pressed. The
  // exception is Incident details, which reads in full either way — collapsed only
  // means its fields aren't editable.
  const [editBasics, setEditBasics] = useState(false);
  const [editPatient, setEditPatient] = useState(false);
  const [editIncident, setEditIncident] = useState(false);   // Incident details
  const [editLocation, setEditLocation] = useState(false);
  const [editOnCourse, setEditOnCourse] = useState(false);   // On course incident details
  const [editTreatment, setEditTreatment] = useState(false);
  const [editOther, setEditOther] = useState(false);         // Other, above Status
  // Anchor for the "add safety netting" menu (see the Medical details section).
  const [nettingMenu, setNettingMenu] = useState(null);
  // Allocation has no edit mode: on an existing incident it is changed through the
  // Reallocate dialog, which applies at once (see targetAction).
  // Collapse every section back to read-only (existing incidents open this way),
  // and clear the transient search text a section holds.
  const collapseSections = () => {
    setEditBasics(false); setEditPatient(false); setEditIncident(false);
    setEditLocation(false); setEditOnCourse(false); setEditTreatment(false);
    setEditOther(false);
    setWithWhomSearch("");
  };

  // Incidents are only ever fetched one event at a time: there are thousands in
  // the database, and loading the lot made this page slow to open. Choosing an
  // event is therefore mandatory — nothing is listed until one is selected.
  const load = (eventId = eventFilter) => {
    if (!eventId) { setIncidents([]); return Promise.resolve(); }
    return axios.get(INCIDENTS_API + "/", { params: { event_id: eventId } })
      .then(res => setIncidents(res.data));
  };

  // (Re)load whenever the chosen event changes. The person filters go with the
  // event they were picked from — their options are that event's people.
  useEffect(() => {
    setCreatedBy(null);
    setAllocatedTo(null);
    setLoading(!!eventFilter);
    load(eventFilter).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter]);

  // Which of the listed incidents a Race Organiser had a hand in. The page can't
  // work this out — it needs the event's roster and its team membership — so it
  // asks the endpoint the report is drawn from, and keeps only the ids (the rows
  // themselves come back redacted, which is right for the report and wrong for
  // the table a Super Admin is looking at). Fetched only while that filter is
  // selected, since it is a second pass over the event's incidents; re-run when
  // they reload, so reallocating one moves it in or out of the filter.
  const wantRaceOrganiser = canWrapUp && !!eventFilter && wrapUp === WRAP_UP_RACE_ORGANISER;
  useEffect(() => {
    if (!wantRaceOrganiser) { setRaceOrganiserIds(null); return undefined; }
    let live = true;
    setRaceOrganiserIds(null);
    axios.get(`${INCIDENTS_API}/race-organiser`, { params: { event_id: eventFilter } })
      .then(res => { if (live) setRaceOrganiserIds(new Set(res.data.map(i => i.id))); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantRaceOrganiser, eventFilter, incidents]);

  useEffect(() => {
    axios.get(EVENTS_API + "/").then(res => setEvents(res.data));
    // Deep-link support: ?event=<id> pre-filtered via this state's initial value;
    // ?open=<id> fetches that incident and switches to its event.
    const openId = searchParams.get("open");
    if (openId) openIncidentById(Number(openId));
    if (searchParams.get("event") || openId) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the events the signed-in user attends (from their attendances). Used to
  // scope user view and to pick the default event filter in either view.
  useEffect(() => {
    if (myPersonLoading) { setMyEventsReady(false); return; }
    if (!myPerson?.id) { setMyEvents([]); setMyTeamIds(new Set()); setMyEventsReady(true); return; }
    setMyEventsReady(false);
    axios.get(ATT_API + "/", { params: { personnel_id: myPerson.id } }).then(res => {
      // Only an *active* attendance counts. Being flagged Available (or having no
      // status) is an offer to work, not a role on the event, and confers no
      // permissions at all — so such an event is never offered here, exactly as it
      // isn't listed on My events. The backend agrees (permissions.ACTIVE_STATUSES
      // via is_active_attendance), so an Available event could only ever have
      // listed nothing.
      const active = res.data.filter(
        a => a.role && ACTIVE_ATTENDANCE_STATUSES.includes(a.status));
      const byId = new Map();
      active.forEach(a => { if (a.event) byId.set(a.event.id, a.event); });
      setMyEvents([...byId.values()]);
      // The same rows carry the user's team on each event, which is what makes an
      // incident allocated to a team count as theirs (see allocatedToMe below).
      setMyTeamIds(new Set(active.map(a => a.event_team_id).filter(id => id != null)));
      setMyEventsReady(true);
    });
  }, [myPersonLoading, myPerson?.id]);

  // Someone who reaches incidents only through the events they attend loses that
  // access shortly after each event (enforced in authz.py), so their events are
  // narrowed to the ones still in reach — keeping historical events they worked on
  // out of the picker entirely. Only a global role that grants every incident
  // escapes the window, since that grant isn't time-limited; any other global role
  // (Events Manager, say) gives no incident access of its own, so its holder is as
  // bound by the window as anyone. `can` with no event is the global-context check,
  // which is the one thing authz.incident_visibility consults globally.
  const seeAllIncidents = can("Incidents.viewAll");
  const attendedEvents = seeAllIncidents ? myEvents : myEvents.filter(withinAccessWindow);

  // The same permission asked of the event actually being looked at, which is a
  // different question from `seeAllIncidents` above — that one is global on
  // purpose, because only a global grant escapes the access window.
  //
  // Two things hang off this: the map, and the incident-type filter. Both are
  // ways of reading a whole event's caseload at once rather than of finding one
  // incident, and for a reader with a discovery-only role they would describe the
  // handful of incidents they happen to have touched as though it were the day.
  const canViewAllHere =
    !!eventFilter && can("Incidents.viewAll", { eventId: Number(eventFilter) });
  // What's actually rendered. Driven by the permission as well as by the choice,
  // so switching to an event they don't hold viewAll on drops them back to the
  // list rather than stranding them on a blank panel.
  const mapView = canViewAllHere && view === VIEW_MAP;

  // The event's pre-defined locations, for the map's pins. Fetched only while the
  // map is up, since nothing else on the page needs them at this scope; that also
  // means switching back and forth re-fetches, which for a handful of rows is
  // cheaper than keeping a cache honest.
  useEffect(() => {
    if (!mapView || !eventFilter) {
      setMapLocations([]); setMapLocationsFailed(false); setMapRoutes([]); setMapExtrasLoaded(false);
      return undefined;
    }
    let live = true;
    setMapExtrasLoaded(false);
    // Gated EventLocations.view server-side, which every holder of
    // Incidents.viewAll also has — but if it does fail it should cost the pins,
    // not throw a toast over the map the reader is looking at.
    const locs = axios.get(LOCATIONS_API + "/", { params: { event_id: eventFilter }, suppressErrorToast: true })
      .then(res => { if (live) { setMapLocations(res.data.sort(sortByLocationType)); setMapLocationsFailed(false); } })
      .catch(() => { if (live) { setMapLocations([]); setMapLocationsFailed(true); } });
    // The event's course routes, drawn under the dots (Events.view — likewise
    // held by everyone who sees the map). A failure just means no lines.
    const routes = axios.get(ROUTES_API + "/", { params: { event_id: eventFilter, geometry: true }, suppressErrorToast: true })
      .then(res => { if (live) setMapRoutes(res.data || []); })
      .catch(() => { if (live) setMapRoutes([]); });
    // The map fits its view once, so it waits for both: fitted to the incidents
    // alone it would sit on the finish and miss the course.
    Promise.allSettled([locs, routes]).then(() => { if (live) setMapExtrasLoaded(true); });
    return () => { live = false; };
  }, [eventFilter, mapView]);

  // In the User view the page is scoped to the reader's own events — except for
  // someone whose global role already grants every incident, where the restriction
  // protects nothing and only hides events they can see plainly in the Admin view
  // (and, for a Super Admin with a single attendance, made the picker look broken).
  const eventScopedToMine = userMode && !seeAllIncidents;

  // Events offered in the filter and the Add dialog.
  const availableEvents = eventScopedToMine ? attendedEvents : events;
  const myEventIds = new Set(attendedEvents.map(e => e.id));
  // The event whose incidents are listed — used to name the export file.
  const selectedEvent = eventFilter
    ? (availableEvents.find(e => e.id === Number(eventFilter))
       || events.find(e => e.id === Number(eventFilter)))
    : undefined;

  // Apply the default event filter once, unless a deep link / prior choice
  // already set it: prefer the last event used (localStorage) if still
  // selectable, else the user's nearest attended event.
  const eventDefaultApplied = useRef(false);
  // Guards the one-time pre-fill of a new incident's location (reset per openNew).
  const locationDefaultApplied = useRef(false);
  useEffect(() => {
    if (eventDefaultApplied.current) return;
    if (eventFilter) { eventDefaultApplied.current = true; return; }
    // Wait until the selectable events and the user's attended events are known.
    if (!myEventsReady || availableEvents.length === 0) return;
    const storedId = Number(lsGet(LS_LAST_EVENT));
    if (storedId && availableEvents.some(e => e.id === storedId)) {
      setEventFilter(storedId);
    } else {
      const near = closestAttendedEvent(attendedEvents);
      if (near && availableEvents.some(e => e.id === near.id)) setEventFilter(near.id);
    }
    eventDefaultApplied.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEventsReady, availableEvents, attendedEvents, eventFilter]);

  // Personnel (for allocation) and participants (for the patient picker) depend
  // on the form's selected event.
  useEffect(() => {
    if (!form.event_id) {
      setAttendances([]); setParticipants([]); setLocations([]); setTeams([]); setRaces([]);
      return;
    }
    axios.get(ATT_API + "/", { params: { event_id: form.event_id } })
      .then(res => setAttendances(res.data));
    axios.get(TEAMS_API + "/", { params: { event_id: form.event_id } })
      .then(res => setTeams(res.data));
    // Races carry the live-tracking link prefixes. An event with none configured
    // simply doesn't offer the tracking button, so this failing isn't worth a toast.
    axios.get(RACES_API + "/", { params: { event_id: form.event_id }, suppressErrorToast: true })
      .then(res => setRaces(res.data))
      .catch(() => setRaces([]));
    axios.get(PARTICIPANTS_API + "/", { params: { event_id: form.event_id } })
      .then(res => setParticipants(res.data));
    axios.get(LOCATIONS_API + "/", { params: { event_id: form.event_id } })
      .then(res => setLocations(res.data.sort(sortByLocationType)));
  }, [form.event_id]);

  // Whether this reader gets the sticky location default on the given event: only
  // the roles that stay in one place do (STICKY_LOCATION_ROLES). Read off their
  // *active* attendance role, so a global role on its own doesn't turn it on —
  // someone raising an incident from a desk has no usual location either.
  const stickyLocationRole = (eventId) => {
    if (eventId == null) return false;
    const er = myRoles?.event_roles?.[String(eventId)];
    return !!er?.active && STICKY_LOCATION_ROLES.includes(er.role);
  };

  // The incident type a new incident starts on: Medical Minor for someone whose
  // role on that event is a clinical one, because that is what they are nearly
  // always recording. Read off the *active* attendance role exactly as
  // stickyLocationRole is, so a global role alone doesn't assume it — a Clinical
  // Manager raising an incident from a desk is as likely to be filing anything
  // else. Blank for everyone else, as before.
  const defaultIncidentType = (eventId) => {
    if (eventId == null) return "";
    const er = myRoles?.event_roles?.[String(eventId)];
    return (er?.active && CLINICAL_ATTENDANCE_ROLES.includes(er.role))
      ? DEFAULT_CLINICAL_INCIDENT_TYPE : "";
  };

  // New incident: once its event's locations load, pre-fill the pre-defined
  // location used on the last incident created (if it belongs to this event and
  // still exists). Applied at most once per open, so the user can clear it.
  useEffect(() => {
    if (editing !== "new" || locationDefaultApplied.current || locations.length === 0) return;
    if (!stickyLocationRole(form.event_id)) return;
    let stored;
    try { stored = JSON.parse(lsGet(LS_LAST_LOCATION) || "null"); } catch { stored = null; }
    if (!stored || Number(stored.eventId) !== Number(form.event_id)) return;
    const match = locations.find(l => l.id === stored.locationId);
    if (match) {
      onSelectLocation(match);
      // Treat the pre-fill as the baseline, so it isn't flagged as unsaved.
      setInitialForm(f => ({ ...f, ...locationFields(match) }));
      locationDefaultApplied.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, editing, form.event_id, myRoles]);

  // This patient's incident history on this event, for the panel under the patient
  // picker and the "drugs given" list beside it.
  //
  // Fetched rather than picked out of the page's own `incidents`, which holds only
  // what the reader's own list contains: on a new incident that excludes this
  // patient's history entirely, because nothing associates the reader with the
  // patient until the save — which is why the panel used to look like a privilege
  // of the viewAll roles. The endpoint gates on Incidents.view for the event, held
  // by everyone who can raise an incident, and every row it returns can be opened.
  //
  // Both halves of the patient key are sent (entry-list link and race number, see
  // patientKey): either identifies them, and an incident whose patient was typed in
  // by hand has only the number. The race number is a field being typed, hence the
  // debounce — a fetch per keystroke otherwise.
  useEffect(() => {
    const eventId = form.event_id;
    const pid = form.patient_participant_id;
    const number = (form.patient_race_number || "").trim();
    if (editing == null || !eventId || (pid == null && !number)) {
      setPatientHistory([]);
      return;
    }
    let active = true;
    const timer = setTimeout(() => {
      axios.get(`${INCIDENTS_API}/patient-history/${eventId}`,
                { params: { participant_id: pid ?? undefined,
                            race_number: number || undefined } })
        .then(res => { if (active) setPatientHistory(res.data); })
        .catch(() => { if (active) setPatientHistory([]); });
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [editing, form.event_id, form.patient_participant_id, form.patient_race_number]);

  const eventLabel = (e) => e?.label_short || e?.label_long || (e ? `#${e.id}` : "—");

  // Match a person by email when both sides have one (exact); otherwise fall back
  // to name, since the created/allocated/note snapshots may carry only a name.
  const myName = (me.name || "").trim().toLowerCase();
  const myEmail = (me.email || "").trim().toLowerCase();
  const hasIdentity = !!(myName || myEmail);
  const personMatch = (email, name) => {
    if (myEmail && email) return email.toLowerCase() === myEmail;
    return !!myName && (name || "").toLowerCase() === myName;
  };

  // Allocated to the reader — the narrowest scope. A team allocation is theirs if
  // they're in that team, which works from their attendances and so doesn't need a
  // name or email; a personal one is matched on identity. The two are exclusive
  // (an incident is allocated to a person or a team, never both), so a team's name
  // is never compared against the reader's own.
  const allocatedToMe = (i) =>
    i.allocated_to_team_id != null
      ? myTeamIds.has(i.allocated_to_team_id)
      : hasIdentity && personMatch(i.allocated_to_email, i.allocated_to_name);
  // Anything they've had a hand in: allocated to them, raised by them, noted on by
  // them, or shared with them. This is the set the server treats as "theirs" for
  // visibility, minus the same-participant incidents it adds on top — which is
  // exactly why the widest scope isn't the same as this one for a discovery-only
  // role.
  //
  // `shared_with_me` comes from the server (nothing on the incident itself names
  // who it was shared with) and has to be here, not only in the widest scope: the
  // User view opens on this filter, so without it someone texted a link would open
  // the incident and then not find it in their own list.
  const interactedWith = (i) =>
    allocatedToMe(i) || !!i.shared_with_me ||
    (hasIdentity && (personMatch(i.created_by_email, i.created_by_name) ||
                     (i.notes || []).some(n => personMatch(n.added_by_email, n.added_by))));

  const inScope = (i) => {
    if (scope === INCIDENT_SCOPE_ALLOCATED) return allocatedToMe(i);
    if (scope === INCIDENT_SCOPE_INTERACTED) return interactedWith(i);
    return true;  // INCIDENT_SCOPE_ALL — the server's own limit is the only one
  };
  const scopeOption = INCIDENT_SCOPES.find(o => o.value === scope);
  const wrapUpOption = WRAP_UP_FILTERS.find(o => o.value === wrapUp);

  // The wrap-up filter, if one is selected. The two consent questions are fields
  // on the incident; the Race Organiser one is the server's answer, and until it
  // arrives the list is empty rather than unfiltered — showing everything while a
  // narrowing filter is on would misreport what is being looked at (and would
  // then be what an export was taken from).
  const wrapUpOk = (i) => {
    if (!canWrapUp || wrapUp === WRAP_UP_NONE) return true;
    if (wrapUp === WRAP_UP_PHOTO_CONSENT) return !!i.consent_teaching_images;
    if (wrapUp === WRAP_UP_FOLLOWUP_CONSENT) return !!i.consent_followup;
    if (wrapUp === WRAP_UP_RACE_ORGANISER) return !!raceOrganiserIds?.has(i.id);
    return true;
  };

  // The listed event's clinical gate, for the "Pending clinical review" filter and
  // the Medical details ones — meaningless to someone who isn't sent those fields
  // in the first place (they arrive blank, so every row would read "none").
  const canClinicalListed = can("Incidents.viewClinical",
                                { eventId: eventFilter ? Number(eventFilter) : undefined });

  // The record search reads rich text as plain text, which is too much work to
  // redo for every row on every keystroke. So each incident's text is built once
  // per load — and only once somebody searches, so the everyday path pays nothing
  // — and the query itself is deferred, so typing stays ahead of the filtering.
  const recordQuery = useDeferredValue(recordFilter);
  const recordTermList = searchTerms(recordQuery);
  const recordActive = recordTermList.length > 0;
  const recordIndex = useMemo(
    () => (recordActive ? new Map(incidents.map(i => [i.id, recordText(i)])) : null),
    [incidents, recordActive]);

  // The panel's person pickers, built only while it's open.
  const creatorOpts = useMemo(() => (moreOpen ? creatorOptions(incidents) : []),
                              [incidents, moreOpen]);
  const allocationOpts = useMemo(() => (moreOpen ? allocationOptions(incidents) : []),
                                 [incidents, moreOpen]);
  // A sub-type is a question about one detail type, not "any" or "none".
  const detailTypeChosen = INCIDENT_DETAIL_TYPES.includes(detailTypeFilter);
  const subtypeOpts = moreOpen && detailTypeChosen
    ? subtypeFilterOptions(incidents, detailTypeFilter, INCIDENT_SUBTYPES[detailTypeFilter] || [])
    : [];

  // The panel's filters, each deferring to its own gate as typeOk does below.
  const moreOk = (i) =>
    createdByMatches(createdBy, i)
    && allocatedToMatches(allocatedTo, i)
    && DEPLOYMENT_FIELDS.every(([key]) => choiceMatches(deployFilter[key], i[key]))
    && (!canClinicalListed || (choiceMatches(detailTypeFilter, i.detail_type)
                               && choiceMatches(subtypeFilter, i.detail_subtype)))
    && (!recordIndex || recordMatches(recordIndex.get(i.id) ?? "", recordTermList));

  const filtered = incidents.filter(i => {
    // The User view lists nothing until an event is chosen, and — unless a global
    // role already covers every event — only among the events the reader attends.
    if (userMode && !eventFilter) return false;
    if (eventScopedToMine && !myEventIds.has(i.event_id)) return false;
    // The fetch is already event-scoped; this keeps the previous event's rows off
    // screen while a switch is still in flight.
    const eventOk = !eventFilter || String(i.event_id) === String(eventFilter);
    const statusOk = !statusFilter || i.status === statusFilter;
    // Deferring to the permission the way wrapUpOk defers to canWrapUp: if the
    // filter isn't on offer it can't be narrowing the list behind the scenes.
    const typeOk = !canViewAllHere || !typeFilter || i.incident_type === typeFilter;
    // The main box finds a patient — by name or race number, the two halves of
    // the Patient column. What was written about them is the record search's.
    const textOk = patientMatches(i, textFilter);
    const reviewOk = !pendingReview
      || (!i.clinical_review_at && needsClinicalReview(i));
    return eventOk && statusOk && typeOk && textOk && reviewOk && wrapUpOk(i) && inScope(i)
      && moreOk(i);
  });

  // What the panel is currently narrowing by, as chips — shown under the bar while
  // the panel is shut, so a filter out of sight is never a filter out of mind.
  // Gated exactly as the filters themselves are, so a chip can't claim a filter
  // that isn't applying.
  const choiceText = (v) => (v === CHOICE_ANY ? "any recorded"
    : v === CHOICE_NONE ? "none recorded" : v);
  const moreActive = [
    canViewAllHere && typeFilter && {
      key: "type", label: `Type: ${typeFilter}`, clear: () => setTypeFilter("") },
    canWrapUp && wrapUpOption && {
      key: "wrapUp", label: `Wrap-up: ${wrapUpOption.label}`, clear: () => setWrapUp(WRAP_UP_NONE) },
    createdBy && {
      key: "createdBy", label: `Created by: ${createdBy.label}`, clear: () => setCreatedBy(null) },
    allocatedTo && {
      key: "allocatedTo", label: `Allocated to: ${allocatedTo.label}`, clear: () => setAllocatedTo(null) },
    ...DEPLOYMENT_FIELDS.map(([key, label]) => deployFilter[key] && {
      key, label: `${label}: ${choiceText(deployFilter[key])}`,
      clear: () => setDeployFilter(f => ({ ...f, [key]: "" })) }),
    canClinicalListed && detailTypeFilter && {
      key: "detailType", label: `Medical details: ${choiceText(detailTypeFilter)}`,
      clear: () => { setDetailTypeFilter(""); setSubtypeFilter(""); } },
    canClinicalListed && subtypeFilter && {
      key: "subtype", label: `Sub-type: ${subtypeFilter}`, clear: () => setSubtypeFilter("") },
    recordFilter.trim() && {
      key: "record", label: `Record contains: ${recordFilter.trim()}`, clear: () => setRecordFilter("") },
  ].filter(Boolean);
  const clearMore = () => moreActive.forEach(f => f.clear());

  // Narrow the table (esp. on mobile) by dropping a column the filter already
  // pins to a single value. There is no Event column at all now that the list is
  // always one event; Status goes when it's filtered to one value, and Type the
  // same way once the type filter has pinned it.
  const showStatusCol = !statusFilter;
  const showTypeCol = !(canViewAllHere && typeFilter);
  const colCount = 4 + (showTypeCol ? 1 : 0) + (showStatusCol ? 1 : 0);

  // The notes and the drugs given travel together: both are things written against
  // the incident after it exists, and both are what an action may have just added.
  const refreshNotes = (id) => {
    axios.get(`${INCIDENTS_API}/${id}/drugs`, { suppressErrorToast: true })
      .then(res => setDrugs(res.data)).catch(() => {});
    return axios.get(NOTES_API + "/", { params: { incident_id: id } }).then(res => setNotes(res.data));
  };

  // A note has just been added — or a drug given, corrected or voided. Reload the
  // notes and drugs, and reflect the clinical review the server cleared: anything
  // added to an incident makes a prior review stale.
  const noteAdded = () => {
    if (typeof editing !== "number") return;
    refreshNotes(editing);
    setForm(f => ({ ...f, clinical_review: false }));
    setCurrent(c => (c ? { ...c, clinical_review_by: null, clinical_review_at: null } : c));
    // Adding to an incident reached from a patient-history row is the whole point
    // of that link — it's what makes the held draft worth asking about.
    markWroteToFollowed();
  };

  // Post whatever is in the New note box, if anything. Always returns a promise so
  // it can be chained (see save(), which adds a note the user typed but never
  // pressed Add Note on); resolves to null when there was nothing to post. Clears
  // the box on success, so a chained call that then fails can't post it twice.
  const postDraftNote = () => {
    const details = newNote.trim();
    if (!details || typeof editing !== "number" || !canNote) return Promise.resolve(null);
    return axios.post(NOTES_API + "/", {
      incident_id: editing,
      details,
      added_by: me.name || null,
      added_by_email: me.email || null,
    }).then((res) => {
      setNewNote("");
      noteAdded();
      return res;
    });
  };

  // The box is only cleared once the note comes back, so without the lock a
  // double-tap posts it twice.
  const addNote = () => runWrite(postDraftNote);

  const deleteNote = (id) =>
    axios.delete(`${NOTES_API}/${id}`).then(() => refreshNotes(editing));

  // The signed-in user's reminder for a given incident (at most one), if any.
  const refreshIncidentReminder = (id) =>
    axios.get(REMINDERS_API + "/", { params: { incident_id: id } })
      .then(res => setIncidentReminder(res.data[0] || null))
      .catch(() => setIncidentReminder(null));

  const deleteIncidentReminder = () => {
    if (!incidentReminder) return;
    axios.delete(`${REMINDERS_API}/${incidentReminder.id}`).then(() => {
      setConfirmDeleteReminder(false);
      if (typeof editing === "number") refreshIncidentReminder(editing);
    });
  };

  // --- Phone Finder ---------------------------------------------------------
  // Requests texted to a casualty so their phone can share its GPS position.
  const refreshPhoneFinds = (id) =>
    axios.get(`${INCIDENTS_API}/${id}/phone-find`, { suppressErrorToast: true })
      .then(res => setPhoneFinds(res.data || []))
      .catch(() => setPhoneFinds([]));

  const openPhoneFind = () => {
    setPhoneFindLink(null);
    // Pre-fill with the patient's own number when there is one, else the
    // free-text alternative phones (the operator confirms/edits either way).
    setPhoneFindPhone((form.patient_phone || "").trim()
      || (form.alternative_phones || "").trim());
    setPhoneFindOpen(true);
  };

  const sendPhoneFind = () => {
    if (typeof editing !== "number") return;
    const phone = (phoneFindPhone || "").trim();
    if (!phone) { notify("Enter the casualty's mobile number.", "error"); return; }
    setPhoneFindBusy(true);
    axios.post(`${INCIDENTS_API}/${editing}/phone-find`, { phone })
      .then(res => {
        setPhoneFindLink(res.data.link);
        setPhoneFindOpen(false);
        notify(res.data.sms_sent
          ? "Locate link texted to the casualty."
          : `Request created, but the SMS could not be sent${res.data.sms_error ? `: ${res.data.sms_error}` : ""}. You can copy the link below.`,
          res.data.sms_sent ? "success" : "warning");
        refreshPhoneFinds(editing);
      })
      .catch(() => {}) // the global interceptor surfaces the error
      .finally(() => setPhoneFindBusy(false));
  };

  // Fill the incident location fields from a received phone-find fix, then open
  // the location editor and derive the other coordinate systems from it.
  const applyPhoneFindLocation = (pf) => {
    if (pf.latitude == null || pf.longitude == null) return;
    const ll = `${pf.latitude.toFixed(5)}, ${pf.longitude.toFixed(5)}`;
    const next = {
      ...form,
      lat_long: ll,
      location_grid_ref: pf.grid_ref || form.location_grid_ref,
      // Note where the fix came from, but don't overwrite an existing note.
      location_details: (form.location_details || "").trim() || "Populated from Phone Finder",
    };
    setForm(next);
    setEditLocation(true);
    fromLatLng(next); // fills what3words, grid ref & postcode from the lat/long
  };

  // Open an incident by id (from the "My reminders" list or an ?open= link) —
  // reuse the loaded row if present, else fetch it. Since the list only holds one
  // event, also switch to the incident's event so it's there behind the dialog.
  const openIncidentById = (id) => {
    const found = incidents.find(x => x.id === id);
    if (found) { openEdit(found); return; }
    axios.get(`${INCIDENTS_API}/${id}`).then(res => {
      openEdit(res.data);
      selectEvent(res.data.event_id);
    }).catch(() => {});
  };

  const openNew = () => {
    // Pre-select the event currently in the filter (the last/nearest event); the
    // location-default effect then pre-fills the last location used for it.
    // Both defaults are part of the baseline, so a dialog opened and cancelled
    // without touching anything doesn't count as unsaved changes.
    const blank = eventFilter
      ? { ...BLANK, event_id: Number(eventFilter),
          incident_type: defaultIncidentType(Number(eventFilter)) }
      : BLANK;
    setForm(blank);
    setInitialForm(blank);
    setCurrent(null);
    setPickedLocation(null); setGrUrl("");
    setNotes([]); setDrugs([]); setNewNote("");
    setIncidentReminder(null);
    setPhoneFinds([]); setPhoneFindLink(null);
    locationDefaultApplied.current = false;
    setHeld(null); wroteToFollowed.current = false;  // a deliberate fresh start
    collapseSections();
    // The event is nearly always already settled — the list can only show one, so
    // the filter has named it and the form above is pre-filled with it. Basics
    // therefore opens as the read-only line with an Edit button, like the patient
    // and location sections. With no event in the filter there's a required choice
    // to make, so it opens on the picker instead.
    setEditBasics(!eventFilter);
    setEditing("new");
  };

  /**
   * Show an existing incident in the dialog.
   *
   * Two options, both for `saveAndContinue` — which reopens the dialog on an
   * incident the user is part way through writing rather than one they picked off
   * the list. `collapse: false` leaves the sections as they had them open, since
   * they haven't finished with them; `fetch: false` skips the re-read of a record
   * the caller already holds fresh from the server.
   */
  const openEdit = (i, { fetch = true, collapse = true } = {}) => {
    const loaded = {
      event_id: i.event_id ?? "",
      patient_name: i.patient_name ?? "",
      patient_race_number: i.patient_race_number ?? "",
      patient_age: i.patient_age ?? "",
      patient_sex: i.patient_sex ?? "",
      patient_race: i.patient_race ?? "",
      patient_phone: i.patient_phone ?? "",
      patient_email: i.patient_email ?? "",
      patient_participant_id: i.patient_participant_id ?? null,
      incident_type: i.incident_type ?? "",
      incident_summary: i.incident_summary ?? "",
      location_type: i.location_type ?? "",
      location_details: i.location_details ?? "",
      location_what3words: i.location_what3words ?? "",
      location_grid_ref: i.location_grid_ref ?? "",
      location_postcode: i.location_postcode ?? "",
      lat_long: combineLatLng(i.location_latitude, i.location_longitude),
      detail_type: i.detail_type ?? "",
      detail_subtype: i.detail_subtype ?? "",
      detail_other: i.detail_other ?? "",
      incident_details: i.incident_details ?? "",
      response_team_deployment: i.response_team_deployment ?? "",
      mountain_safety_deployment: i.mountain_safety_deployment ?? "",
      disposal_transport: i.disposal_transport ?? "",
      disposal_destination: i.disposal_destination ?? "",
      with_whom: i.with_whom ?? "",
      phone_status: i.phone_status ?? "",
      alternative_phones: i.alternative_phones ?? "",
      experience: i.experience ?? "",
      weather: i.weather ?? "",
      welfare: i.welfare ?? "",
      patient_consent: i.patient_consent ?? "",
      patient_consent_details: i.patient_consent_details ?? "",
      patient_consent_child_by: i.patient_consent_child_by ?? "",
      treatment_given: i.treatment_given ?? "",
      advised_to_retire: i.advised_to_retire ?? "",
      safety_netting: orderedNetting(i.safety_netting || []),
      allocated_to_personnel_id: i.allocated_to_personnel_id ?? null,
      allocated_to_team_id: i.allocated_to_team_id ?? null,
      consent_teaching_images: i.consent_teaching_images ?? false,
      consent_followup: i.consent_followup ?? false,
      status: i.status ?? "Ongoing",
      clinical_review: !!i.clinical_review_at,
    };
    setForm(loaded);
    setInitialForm(loaded);
    setCurrent(i);
    setPickedLocation(null); setGrUrl("");
    setNewNote("");
    setNotes([]); setDrugs([]);
    refreshNotes(i.id);
    setIncidentReminder(null);
    refreshIncidentReminder(i.id);
    setPhoneFinds([]); setPhoneFindLink(null);
    if (can("Incidents.phoneFind", { eventId: i.event_id })) refreshPhoneFinds(i.id);
    // Existing incident: start every rarely-changed section collapsed.
    if (collapse) collapseSections();
    setEditing(i.id);
    // Fetch the single incident so the view is recorded in the audit trail
    // (clinical access log) and the read-only created_*/allocation fields are
    // refreshed with the latest server state.
    if (fetch) {
      axios.get(`${INCIDENTS_API}/${i.id}`)
        .then(res => setCurrent(res.data))
        .catch(() => {}); // the global interceptor surfaces any error
    }
  };

  // Everything the dialog needs to be put back exactly as it was: the form and its
  // unsaved-changes baseline, the pre-defined location picked, the Grid Reference
  // Finder box, a note typed but not added, and which sections were expanded. The
  // fetched things (notes, reminder, phone finds) are deliberately *not* kept — on
  // the way back they're re-read, which costs one request and can't show something
  // stale that changed while the other incident was open.
  const dialogSnapshot = () => ({
    editing, current, form, initialForm, pickedLocation, grUrl, newNote,
    sections: { basics: editBasics, patient: editPatient, incident: editIncident,
                location: editLocation, onCourse: editOnCourse, treatment: editTreatment,
                other: editOther },
  });

  const restoreDialog = (snap) => {
    setForm(snap.form); setInitialForm(snap.initialForm);
    setPickedLocation(snap.pickedLocation); setGrUrl(snap.grUrl);
    setNewNote(snap.newNote);
    setEditBasics(snap.sections.basics); setEditPatient(snap.sections.patient);
    setEditIncident(snap.sections.incident); setEditLocation(snap.sections.location);
    setEditOnCourse(snap.sections.onCourse); setEditTreatment(snap.sections.treatment);
    setEditOther(!!snap.sections.other);
    setCurrent(snap.current);
    setNotes([]); setDrugs([]); setIncidentReminder(null); setPhoneFinds([]); setPhoneFindLink(null);
    setEditing(snap.editing);
    // The sticky-location pre-fill has had its moment: whatever location the draft
    // is carrying is the reader's, so the one-time default must not apply itself on
    // the way back and overwrite it.
    locationDefaultApplied.current = true;
    // An existing incident's fetched sides are re-read; a new one has none.
    if (typeof snap.editing === "number") {
      refreshNotes(snap.editing);
      refreshIncidentReminder(snap.editing);
      if (can("Incidents.phoneFind", { eventId: snap.current?.event_id })) {
        refreshPhoneFinds(snap.editing);
      }
    }
  };

  // The clinical reports naming the open incident, listed to whoever sees its
  // medical details; each says whether this reader may open it (`can_open`).
  // Refetched when the record is re-served (Save & continue, which may have raised
  // one automatically) and after one is raised or worked from here.
  const listReports = can("Incidents.viewClinical", { eventId: current?.event_id });
  useEffect(() => {
    setLinkedReports([]);
    if (!listReports || typeof editing !== "number") return;
    let live = true;
    axios.get(`${API_BASE_URL}/clinical-reports/for-incident/${editing}`)
      .then(res => live && setLinkedReports(res.data))
      .catch(() => {});
    return () => { live = false; };
  }, [listReports, editing, current, reportsKey]);

  // Clear the dialog down. Everything transient goes; `held` is the caller's to
  // decide about, so it isn't touched here (see close/discardHeld).
  const clearDialog = () => {
    releaseWrite();  // don't carry a write in flight into the next dialog
    setEditing(null); setCurrent(null); setPickedLocation(null); setGrUrl("");
    setNotes([]); setDrugs([]); setNewNote(""); setConfirmClose(false);
    setIncidentReminder(null); setReminderFormOpen(false); setConfirmDeleteReminder(false);
    setDrugOpen(false); setReportOpen(false); setOpenReportId(null); setTargetAction(null);
    setPhoneFinds([]); setPhoneFindOpen(false); setPhoneFindLink(null); setPhoneFindPhone("");
  };

  // Closing the dialog. With an incident held behind this one (the reader followed
  // a patient-history row to get here) closing means *going back to it*, not
  // leaving — except where they've since added to the incident they followed and
  // the held one is a part-written new incident, which is exactly the case this
  // whole path exists to prevent: the visit is already recorded, so the new
  // incident is probably the duplicate. That is a question, not an assumption —
  // two problems on one patient is an ordinary thing, and guessing wrong throws
  // away typed clinical detail.
  const close = () => {
    if (held) {
      if (wroteToFollowed.current && held.editing === "new") {
        setConfirmKeepHeld(true);
        return;
      }
      returnToHeld();
      return;
    }
    clearDialog();
  };

  const returnToHeld = () => {
    const snap = held;
    setHeld(null); setConfirmKeepHeld(false);
    wroteToFollowed.current = false;
    clearDialog();
    if (snap) restoreDialog(snap);
  };

  const discardHeld = () => {
    setHeld(null); setConfirmKeepHeld(false);
    wroteToFollowed.current = false;
    clearDialog();
    load();  // the incident they added to is in the list behind
  };

  // Follow a patient-history row to that incident, keeping this one to come back
  // to. In place rather than in a new tab: this is read on a phone at an event,
  // and the reader is part way through a record — the dialog they're in is the
  // thing to return to, so the app holds it rather than asking the browser to.
  // Only the *first* hop anchors the return: following a second row on from there
  // keeps the original hold, so Back always means "the record I was writing" and
  // not a trail of incidents to climb out of. Whether anything was written stays
  // sticky across the hops for the same reason.
  const followHistory = (id) => {
    const snap = dialogSnapshot();
    const row = patientHistory.find(i => i.id === id);
    const firstHop = !held;
    clearDialog();
    if (firstHop) { setHeld(snap); wroteToFollowed.current = false; }
    if (row) openEdit(row);
    else openIncidentById(id);
  };

  // Called on every write to the incident currently open, so that returning to a
  // held draft can ask whether it's still needed. Cheap and unconditional — it
  // only means anything while something is held.
  const markWroteToFollowed = () => { if (held) wroteToFollowed.current = true; };

  // Unsaved changes = edited form fields or a draft note not yet added.
  const isDirty = () =>
    JSON.stringify(form) !== JSON.stringify(initialForm) || newNote.trim() !== "";

  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  const requestClose = () => (isDirty() ? setConfirmClose(true) : close());

  const field = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
  });

  // Tick / untick one category of safety-netting advice.
  const toggleSafetyNetting = (name) => {
    setForm(f => {
      const chosen = f.safety_netting || [];
      const next = chosen.includes(name)
        ? chosen.filter(n => n !== name)
        : [...chosen, name];
      return { ...f, safety_netting: orderedNetting(next) };
    });
  };

  // The free-text option. Its text lives in the stored value ("Other: …"), so there
  // is no second piece of state to keep in step — typing edits the list itself, and
  // the unsaved-changes check sees it like any other field. Unticking drops the text
  // with it: the advice wasn't given, so there is nothing to keep.
  const otherNetting = otherNettingText(form.safety_netting);
  const otherNettingOn = (form.safety_netting || []).some(isOtherNetting);
  const setOtherNetting = (value) => {
    setForm(f => {
      const rest = (f.safety_netting || []).filter(n => !isOtherNetting(n));
      const next = value == null ? rest : [...rest, otherNettingValue(value)];
      return { ...f, safety_netting: orderedNetting(next) };
    });
  };
  // What the Add menu still has to offer. Nothing or one entry is the normal
  // answer, so the eight options live behind a button rather than eight rows of
  // ticks, and an option already recorded drops off the menu instead of sitting
  // there ticked — its chip above is where it's unpicked.
  const nettingChosen = form.safety_netting || [];
  const nettingLeft = SAFETY_NETTING.filter(o => !nettingChosen.includes(o.name));

  // Changing the detail type clears a sub-type that no longer belongs to it — bar
  // type "Other", whose only sub-type is Other, so it's set rather than asked. The
  // description of an Other is kept: it still describes the same thing.
  const onDetailType = (e) => {
    const detail_type = e.target.value;
    setForm(f => ({ ...f, detail_type,
                    detail_subtype: detail_type === "Other" ? OTHER_SUBTYPE : "" }));
  };

  // Changing the event resets allocation to the creator (the personnel and team
  // lists both belong to the old event).
  const onEvent = (e) => {
    const eventId = e.target.value;
    setForm(f => ({
      ...f, event_id: eventId,
      allocated_to_personnel_id: null, allocated_to_team_id: null,
      // On a new incident the clinical default follows the event, since the role
      // it reads is per-event — but only into a blank field, never over a chosen
      // type, and never onto an existing incident, whose blank type is a fact
      // about the record rather than a field waiting to be filled.
      incident_type: (editing === "new" && !f.incident_type)
        ? defaultIncidentType(Number(eventId)) : f.incident_type,
    }));
  };

  // Changing the event filter persists it as the sticky default for next time;
  // an explicit choice (including "All") also wins over the auto-default.
  // Select the event whose incidents are listed, remembering it for next time.
  // The load effect refetches; when the event is already selected it won't fire,
  // so callers that need fresh data call load() themselves.
  const selectEvent = (id) => {
    setEventFilter(id === "" ? "" : Number(id));
    eventDefaultApplied.current = true;
    if (id !== "") lsSet(LS_LAST_EVENT, String(id));
  };

  const onEventFilterChange = (e) => selectEvent(e.target.value);

  // Changing the status filter persists it (incl. "All") as next time's default.
  const onStatusFilterChange = (e) => {
    const v = e.target.value;
    setStatusFilter(v);
    lsSet(LS_LAST_STATUS, v);
  };

  // Creator's name/role: from the logged-in user (new) or the stored snapshot (edit).
  const creatorName = editing === "new" ? me.name : (current?.created_by_name || "");
  const creatorRole = editing === "new" ? null : current?.created_by_role;

  // Allocation: the "creator" sentinel, then each of the event's teams ("t:<id>")
  // and each person attending it ("p:<id>"). The two are prefixed because they're
  // separate id spaces sharing one dropdown.
  const allocValue = form.allocated_to_team_id != null ? `t:${form.allocated_to_team_id}`
    : form.allocated_to_personnel_id != null ? `p:${form.allocated_to_personnel_id}`
    : "creator";
  // Everyone on the event may be allocated an incident — including a Mountain
  // Safety attendee, since someone has to be able to record who is dealing with a
  // casualty on the hill. But their role gives them no access to incidents, so
  // they can't open a link to it: `canOpen` is the server's own verdict on that
  // (AttendanceRead.can_view_incidents, which unions their global roles with the
  // attendance), and it drives both the grouping below and whether the SMS is
  // offered at all. They go last, in a group of their own under a heading that says
  // once what they have in common — as in IncidentTargetDialog, which offers the same
  // people for the same reasons.
  //
  // Within each group the list reads by role then surname, matching the event's
  // personnel list and the share/reallocate dialogs — the same people, offered three
  // ways, so they are offered in one order.
  const allocOptions = attendances
    .filter(a => a.person)
    .map(a => ({ id: a.personnel_id, name: `${a.person.forename} ${a.person.surname}`,
                 role: a.role, surnameKey: surnameKey(a.person),
                 canOpen: !!a.can_view_incidents }))
    .sort(sortByAttendanceRole);
  const allocReachable = allocOptions.filter(o => o.canOpen);
  const allocUnreachable = allocOptions.filter(o => !o.canOpen);
  // Only label the groups when there is more than one to tell apart.
  const allocGrouped = teams.length > 0 || allocUnreachable.length > 0;
  const onAlloc = (e) => {
    const v = e.target.value;
    const [kind, id] = v === "creator" ? ["creator", null] : [v.slice(0, 1), Number(v.slice(2))];
    setForm(f => ({ ...f,
      allocated_to_personnel_id: kind === "p" ? id : null,
      allocated_to_team_id: kind === "t" ? id : null }));
  };
  // A team has no role of its own; the field reads "—" while one is allocated.
  const allocRole = form.allocated_to_team_id != null
    ? null
    : form.allocated_to_personnel_id == null
      ? creatorRole
      : (allocOptions.find(o => o.id === form.allocated_to_personnel_id)?.role ?? null);
  // The team currently chosen in the form, when it's still in the event's list.
  const allocTeam = form.allocated_to_team_id != null
    ? (teams.find(t => t.id === form.allocated_to_team_id) || null)
    : null;
  // Who is in a team, for the small print under the picker and the display.
  const teamMemberNames = (t) =>
    (t?.members || []).map(m => `${m.forename} ${m.surname}`).join(", ") || "No one in this team yet";

  // What the Allocation section shows. On a new incident that's the form (the
  // picker below sets it); on an existing one the allocation is only ever changed
  // through the Reallocate dialog, which applies at once — so the two agree, and
  // the stored snapshot is the fallback while the event's people and teams load, or
  // for a target that has since left the event or been deleted (which nulls the id
  // but keeps the name).
  const allocName = (() => {
    if (form.allocated_to_team_id != null) {
      if (allocTeam) return `${allocTeam.name} (team)`;
    } else if (form.allocated_to_personnel_id != null) {
      const person = allocOptions.find(o => o.id === form.allocated_to_personnel_id);
      if (person) return person.name;
    }
    return allocatedLabel(current) || creatorName;
  })();

  // Patient picker: the currently linked participant (if still in the list).
  const selectedParticipant = form.patient_participant_id != null
    ? (participants.find(p => p.id === form.patient_participant_id) || null)
    : null;
  // Under 16, someone else's consent is recorded as well — judged on the age
  // recorded or the entrant's date of birth on the day of the incident.
  const childPatient = isChildPatient(form.patient_age, selectedParticipant?.date_of_birth,
                                      current?.created_at);
  const participantLabel = (p) =>
    [p.name || "(no name)", p.race_number].filter(Boolean).join(" — ");
  // The entrant this patient's race number names, when the incident has no
  // entry-list link. Worth offering, because the link is easily lost without
  // meaning to: MUI clears a single-select Autocomplete's *value* whenever its text
  // is emptied, so backspacing through the picker to search again unlinks the
  // participant — and since the picker fills the patient fields, everything still
  // looks linked. Nothing but the missing eye icon said otherwise, and the watch
  // list flag, the participant view and (until now) the patient's history all
  // quietly stopped working. Offered as one click rather than done silently: the
  // number is typed by hand too, and only an unambiguous match is proposed.
  const sameNumber = (a, b) =>
    (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const numberMatches = (form.patient_participant_id != null
                         || !(form.patient_race_number || "").trim())
    ? []
    : participants.filter(p => sameNumber(p.race_number, form.patient_race_number));
  const linkSuggestion = numberMatches.length === 1 ? numberMatches[0] : null;
  // What identifies this patient for their history: the entry-list link when one
  // was picked, and the race number, which is the entrant's identifier on the day.
  // Either is enough, and the number is the only handle on a patient typed in by
  // hand — which is most of them at an event, and is why the history can't key on
  // the participant link alone. Matched server-side (see the fetch above).
  const hasPatientKey = form.patient_participant_id != null
    || (form.patient_race_number || "").trim() !== "";
  // The same patient's other incidents, from the patient-history endpoint. Not
  // filtered out of the page's own `incidents` list, which holds only what the
  // reader may open: on a new incident that excludes this patient's history
  // entirely, since nothing associates the reader with them until the save.
  const otherPatientIncidents = patientHistory.filter(i => i.id !== editing);
  // Every drug this patient has been given on this event, oldest first — from this
  // incident and from any other incident sharing the participant, so whoever is
  // about to give something can see what they have already had. Voided records
  // were mistakes and are left out.
  //
  // This incident's come from `drugs` (reloaded on every administration, so a drug
  // just given appears at once); the others from the history rows, which carry
  // theirs. Both are already redacted for a reader without the clinical gate, so a
  // Race Organiser sees no drugs here at all.
  const patientDrugs = (!hasPatientKey ? [] : [
    ...standingDrugs(drugs).map(d => ({ ...d, incident: null })),
    ...otherPatientIncidents.flatMap(i => standingDrugs(i.drug_administrations)
      .map(d => ({ ...d, incident: i }))),
  ]).sort((a, b) => (a.given_at || "").localeCompare(b.given_at || ""));
  // Selecting a participant snapshots their details into the patient fields.
  const onSelectParticipant = (p) => {
    if (!p) { setForm(f => ({ ...f, patient_participant_id: null })); return; }
    setForm(f => ({
      ...f,
      patient_participant_id: p.id,
      patient_name: p.name ?? "",
      patient_race: p.race ?? "",
      patient_race_number: p.race_number ?? "",
      patient_age: p.age ?? "",
      patient_sex: p.sex ?? "",
      patient_phone: p.phone ?? "",
      patient_email: p.email ?? "",
    }));
  };

  // Stamp an entrant's name, race number and phone into the "With whom" box, on a
  // line of their own after whatever is already there. The people a casualty is
  // out with are usually entrants themselves, and copying three fields off
  // another screen is slow and is where a phone number gets mistyped.
  //
  // **It writes text and nothing else.** No participant id is kept — the
  // patient's is the only entry-list link an incident has — so nobody named here
  // is matched to another incident, tracked, screening-flagged or exported as a
  // participant. That is the whole of the feature, and the reason it costs
  // nothing beyond this function.
  const addToWithWhom = (p) => {
    if (!p) return;
    const line = [p.name, p.race_number && `#${p.race_number}`, p.phone]
      .filter(Boolean).join(" - ");
    if (!line) return;
    setForm(f => {
      // Trailing whitespace trimmed first, so adding a second entrant to a box
      // that already ends in a newline doesn't leave a blank line between them.
      const existing = (f.with_whom || "").replace(/\s+$/, "");
      return { ...f, with_whom: existing ? `${existing}\n${line}` : line };
    });
  };

  const locationLabel = (l) =>
    [l.name || "(unnamed)", l.type].filter(Boolean).join(" — ");
  // The form fields a pre-defined location fills in.
  const locationFields = (l) => ({
    location_type: l.type ?? "",
    location_details: l.name ?? "",
    location_what3words: l.what3words ?? "",
    location_grid_ref: l.grid_ref ?? "",
    location_postcode: l.postcode ?? "",
    lat_long: combineLatLng(l.latitude, l.longitude),
  });
  // Picking a pre-defined location fills the location fields (still editable).
  const onSelectLocation = (l) => {
    setPickedLocation(l);
    if (!l) return;
    setForm(f => ({ ...f, ...locationFields(l) }));
  };

  // Blank every location field: the pre-defined picker, the type/details, all
  // four coordinate systems and the Grid Reference Finder link box. On an
  // existing incident this is a change that still needs saving; on a new one it
  // clears values pre-populated from the last-used location.
  const clearLocation = () => {
    setPickedLocation(null);
    setGrUrl("");
    setForm(f => ({
      ...f,
      location_type: "",
      location_details: "",
      location_what3words: "",
      location_grid_ref: "",
      location_postcode: "",
      lat_long: "",
    }));
  };

  const num = (v) => (v === "" || v === null ? null : Number(v));
  const str = (v) => (v === "" ? null : v);

  // Compact read-only summaries for the sections that have them. Existing
  // incidents collapse to these until the section's Edit button is pressed, and a
  // new one starts collapsed for Basics, Patient and Location — the event comes
  // from the list's filter, a patient is nearly always picked off the entry list
  // and a location off the event's own list, so the fields behind them are the
  // exception rather than the way in. Every other section of a new incident is
  // still shown in full. Nothing is abbreviated:
  // Incident details and Medical details are what the incident is read for, and
  // the rest is short by nature.
  const isNew = editing === "new";
  // Muted placeholder shown when a collapsed section has nothing to summarise.
  const placeholder = (text) => <Box component="span" sx={{ color: "text.secondary" }}>{text}</Box>;
  // One of the dialog's section headings. The divider rule is kept — it's what
  // gives the form its rhythm — and the label carries the weight instead: bold,
  // uppercase and lightly tracked in Shelter Stone green, so a heading can't be
  // mistaken for the content under it. This dialog is a dozen sections long and is
  // read on a phone at an event, where the plain hairline label was too quiet to
  // find your place by.
  const sectionHeading = (text) => (
    <Divider textAlign="left">
      <Typography variant="subtitle2" component="span"
                  sx={{ color: "primary.main", fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {text}
      </Typography>
    </Divider>
  );
  // "Done" button that re-collapses a section. Offered on a new incident only by
  // the sections that can collapse there — Basics, Patient and Location
  // (`onNewToo`); for the rest, expanded is the only state they have, and a Done
  // that collapsed a required field out of sight would be a trap.
  const doneRow = (onDone, onNewToo = false) => (!isNew || onNewToo) && (
    <Box sx={{ display: "flex" }}>
      <Button size="small" onClick={onDone}>Done</Button>
    </Box>
  );
  const patientSummary = [
    form.patient_name,
    form.patient_race_number && `#${form.patient_race_number}`,
    (form.patient_age !== "" && form.patient_age != null) && `${form.patient_age} yrs`,
    form.patient_sex,
    form.patient_race,
    form.patient_phone,
    form.patient_email,
  ].filter(Boolean).join("  ·  ");
  // Whether this incident has a patient at all. Anything in the patient section
  // counts, picked from the entry list or typed in: an unidentified casualty is
  // still a patient, and is often all that's known when the incident is raised
  // ("male, 40s"). patientSummary is exactly "is there anything to say about the
  // patient", so it's reused rather than restated.
  const hasPatient = form.patient_participant_id != null || patientSummary !== "";
  // Whether anything clinical has actually been written down. The clinical fields
  // are hidden without a patient (see showClinical), and this is what stops that
  // hiding a record: an incident whose details were filled in before the patient
  // section was, or one from before this rule existed, still shows them. Same
  // principle as the On course prompts, which stay visible whatever the location
  // now says. Drugs given count too — they can be the only clinical content there
  // is.
  const clinicalRecorded = !!(
    form.detail_type || form.detail_subtype || (form.treatment_given || "").trim()
    || (form.safety_netting || []).length || form.advised_to_retire
    || form.patient_consent || (form.patient_consent_details || "").trim()
    || (form.patient_consent_child_by || "").trim()
    || form.consent_teaching_images || form.consent_followup || form.clinical_review
    || drugs.length > 0);

  // The patient's live-tracking page, from the event's configured prefix for their
  // race plus their race number (see tracking.js). Derived from the *form*, so it
  // follows a race number just typed in, without needing a save first. Null when
  // the event has no races configured, the race doesn't match one, or there's no
  // number — the button is only offered when there is somewhere to go.
  const trackUrl = trackingUrl(races, form.patient_race, form.patient_race_number);
  // A location value shown read-only, as an external link when we have a URL for it.
  const locValue = (label, value, url) => {
    if (!value) return null;
    return (
      <Typography key={label} variant="body2" component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mr: 2 }}>
        <Box component="span" sx={{ color: "text.secondary" }}>{label}:</Box>
        {url ? (
          <Link href={url} target="_blank" rel="noopener"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
            {value}<OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        ) : value}
      </Typography>
    );
  };

  // Whether this incident has a location at all — the pre-defined one just picked,
  // or anything written in any of the fields. Same shape as hasPatient above, and
  // used for the same purpose: a new incident opens on the picker alone, and
  // switches to the read-only summary as soon as there is something to summarise.
  const hasLocation = !!pickedLocation || !!(
    form.location_type || form.location_details || form.location_what3words
    || form.location_grid_ref || form.location_postcode || form.lat_long);

  // The two pickers, held in variables because a new incident shows each of them
  // on its own (the section collapsed to just the picker) and again inside the
  // full field set — the same element either way, so the two can't drift.
  const participantPicker = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <Autocomplete
        sx={{ flex: 1 }}
        options={participants}
        value={selectedParticipant}
        onChange={(_, v) => onSelectParticipant(v)}
        getOptionLabel={participantLabel}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={!form.event_id}
        noOptionsText={form.event_id ? "No participants — import an entry list on the event" : "Select an event first"}
        renderInput={(params) => (
          <TextField {...params} label="Select from participants"
                     helperText="Search the event entry list by name or race number" />
        )}
      />
      <Tooltip title="View participant details">
        <span>
          <IconButton sx={{ mt: 1 }} disabled={!selectedParticipant}
                      onClick={() => setViewParticipant(selectedParticipant)}>
            <VisibilityIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const locationPicker = (
    <Autocomplete
      options={locations}
      value={pickedLocation}
      onChange={(_, v) => onSelectLocation(v)}
      getOptionLabel={locationLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      disabled={!form.event_id}
      noOptionsText={form.event_id ? "No locations — add them on the event" : "Select an event first"}
      renderInput={(params) => (
        <TextField {...params} label="Use a pre-defined location"
                   helperText="Fills the location fields; you can still edit them" />
      )}
    />
  );

  // "It happened out on the course": the one location that is never pre-defined,
  // so it goes straight to the fields with the type already set — which is also
  // what brings up the On course prompts below.
  const startOnCourseLocation = () => {
    setForm(f => ({ ...f, location_type: "On Course" }));
    setEditLocation(true);
  };

  const commonPayload = () => {
    // Accept DMS/DDM in the lat/long field; store decimal degrees.
    const [lat, lng] = parseLatLng(normalizeLatLng(form.lat_long) || form.lat_long);
    return {
      patient_name: str(form.patient_name),
      patient_race_number: str(form.patient_race_number),
      patient_age: num(form.patient_age),
      patient_sex: str(form.patient_sex),
      patient_race: str(form.patient_race),
      patient_phone: str(form.patient_phone),
      patient_email: str(form.patient_email),
      patient_participant_id: form.patient_participant_id,
      incident_type: str(form.incident_type),
      incident_summary: str(form.incident_summary),
      location_type: str(form.location_type),
      location_details: str(form.location_details),
      location_what3words: str(form.location_what3words),
      location_grid_ref: str(form.location_grid_ref),
      location_postcode: str(form.location_postcode),
      location_latitude: lat,
      location_longitude: lng,
      // The clinical fields are omitted entirely for a reader who can't see them
      // (a Race Organiser), whose form holds blanks where the real values are —
      // sending those blanks back would wipe the treatment notes. The server drops
      // them too (core/clinical.py); this just avoids the pointless round trip.
      ...(canClinical ? {
        detail_type: str(form.detail_type),
        detail_subtype: str(form.detail_subtype),
        // Only an Other is described; a description left over from before the
        // sub-type was changed isn't saved.
        detail_other: form.detail_subtype === OTHER_SUBTYPE ? str(form.detail_other) : null,
        patient_consent: str(form.patient_consent),
        // As with detail_other: details left over from an option that needs none,
        // or a consent-giver from before the age was corrected, aren't saved.
        patient_consent_details: consentNeedsDetails(form.patient_consent)
          ? str(form.patient_consent_details) : null,
        patient_consent_child_by: childPatient ? str(form.patient_consent_child_by) : null,
        treatment_given: str(form.treatment_given),
        advised_to_retire: str(form.advised_to_retire),
        // Null rather than [] when nothing is ticked, following `str` above: the
        // column then stays NULL on the incidents that predate the field, so
        // re-saving one doesn't log a change that wasn't made. normaliseNetting
        // trims the Other entry, which is held verbatim while it's being typed.
        safety_netting: form.safety_netting.length
          ? normaliseNetting(form.safety_netting) : null,
        consent_teaching_images: form.consent_teaching_images,
        consent_followup: form.consent_followup,
        clinical_review: form.clinical_review,
        clinical_review_by: me.name || null,
      } : {}),
      incident_details: str(form.incident_details),
      response_team_deployment: str(form.response_team_deployment),
      mountain_safety_deployment: str(form.mountain_safety_deployment),
      disposal_transport: str(form.disposal_transport),
      disposal_destination: str(form.disposal_destination),
      with_whom: str(form.with_whom),
      phone_status: str(form.phone_status),
      alternative_phones: str(form.alternative_phones),
      experience: str(form.experience),
      weather: str(form.weather),
      welfare: str(form.welfare),
      status: form.status,
      // No allocation here: on a new incident it's added by the caller below, and
      // on an existing one it belongs to the Reallocate dialog, which applies at
      // once. Leaving it out of the ordinary Save is what stops the two fighting —
      // the server keeps whatever is stored for a field a PUT doesn't mention.
    };
  };

  // Report what happened to an allocation or share text, from the outcome the
  // server returns per recipient — so the toast says what really went out instead
  // of assuming it worked. It's on the incident's notes either way.
  const reportSms = (sms, done) => {
    if (!sms || (!sms.sent && !sms.failed)) { notify(done, "success"); return; }
    const why = (sms.recipients || []).find(r => !r.sent)?.error || "the text failed";
    if (!sms.failed) notify(`${done} ${sms.target} was texted a link to it.`, "success");
    else if (!sms.sent) notify(`${done} But ${sms.target} could not be texted: ${why}.`, "warning");
    else notify(`${done} ${sms.sent} of ${sms.sent + sms.failed} in ${sms.target} were `
                + `texted, ${sms.failed} could not be reached.`, "warning");
  };

  // Apply the Reallocate dialog's choice: a PUT of just the allocation, plus the
  // text it asked for. The clinical review is left out on purpose: a PUT that
  // doesn't mention it, and touches nothing the review read, keeps it as stored
  // (routers/incident.py) — and an unsaved tick or untick in the form is the
  // form's Save to apply, not the reallocation's.
  const reallocate = ({ personnelId, teamId, notify: notifySms, message }) =>
    axios.put(`${INCIDENTS_API}/${editing}`, {
      allocated_to_personnel_id: personnelId,
      allocated_to_team_id: teamId,
      notify_allocation: notifySms,
      allocation_message: message,
    }).then(res => {
      const updated = res.data;
      setCurrent(updated);
      // Keep the form (and its unsaved-changes baseline) in step, since the
      // section reads the allocation off the form.
      const ids = {
        allocated_to_personnel_id: updated.allocated_to_personnel_id ?? null,
        allocated_to_team_id: updated.allocated_to_team_id ?? null,
      };
      setForm(f => ({ ...f, ...ids }));
      setInitialForm(f => ({ ...f, ...ids }));
      reportSms(updated.allocation_sms, "Incident reallocated.");
      refreshNotes(editing);  // the reallocation wrote one
      load();
    });

  // Apply the Share dialog's choice. The share is its own endpoint: it grants the
  // recipient sight of this one incident and writes its own note, and the text is
  // optional (see components/IncidentTargetDialog.jsx).
  const shareIncident = ({ personnelId, teamId, notify: notifySms, message }) =>
    axios.post(`${INCIDENTS_API}/${editing}/share`, {
      personnel_id: personnelId, team_id: teamId,
      notify: notifySms, message,
    }).then(res => {
      const { target, sent, failed, recipients } = res.data || {};
      reportSms({ target, sent, failed, recipients }, `Shared with ${target}.`);
      refreshNotes(editing);
      load();
    });

  // File the new incident and refresh the list behind the dialog. What happens to
  // the dialog itself is the caller's: Save closes it, Save & continue reopens it
  // on the record that was just created (see saveAndContinue).
  const createIncident = (then) => {
    const payload = {
      event_id: Number(form.event_id),
      created_by_name: me.name || null,
      created_by_email: me.email || null,
      ...commonPayload(),
    };
    // Remember this event + picked location as sticky defaults for next time.
    lsSet(LS_LAST_EVENT, String(payload.event_id));
    // No pre-defined location on this one clears the default rather than
    // keeping an older one: the next incident then starts blank, which is the
    // truthful answer to "where was the last one?".
    if (pickedLocation) {
      lsSet(LS_LAST_LOCATION, JSON.stringify({
        eventId: payload.event_id, locationId: pickedLocation.id,
      }));
    } else {
      lsDel(LS_LAST_LOCATION);
    }
    // A new incident's allocation is part of creating it, not a reallocation, so
    // it comes straight off the form's picker and texts nobody.
    payload.allocated_to_personnel_id = form.allocated_to_personnel_id;
    payload.allocated_to_team_id = form.allocated_to_team_id;
    return axios.post(INCIDENTS_API + "/", payload).then(res => {
      // The list holds one event, which may not be the one this was filed
      // under — switch to it (a no-op switch won't refetch, so load() then).
      if (Number(payload.event_id) === Number(eventFilter)) load();
      else selectEvent(payload.event_id);
      then(res.data);
    });
  };

  // Save without leaving: file the incident, then carry straight on editing the
  // record that comes back.
  //
  // This is how a drug given, a note, a photo, a reminder or a share is recorded
  // while filing an incident. All of them are written *against* the incident — a
  // drug administration IS its note (core/incident_drugs.py), and a photo is a
  // Document bound to the incident id (core/photos.py) — so there has to be a
  // record to hang them on, and until now that meant saving, finding the incident
  // in the list and opening it again.
  //
  // Nothing about the created incident is special: it is reopened through the same
  // openEdit the list uses, so the dialog is in exactly the state it would be in
  // had it been opened from the table. The one departure is that it isn't fetched
  // again — the POST already returned the whole record, and a `read` audit row
  // naming the person who created it a second earlier is noise on the clinical
  // access trail (see routers/incident.get_incident).
  const saveAndContinue = () => runWrite(() => createIncident(created => {
    openEdit(created, { fetch: false, collapse: false });
    notify("Incident saved. You can now add notes, photos and drugs given.", "success");
  }));

  const save = () => runWrite(() => {
    if (editing === "new") {
      return createIncident(() => close());
    } else {
      // Built before the note is posted: posting one clears the clinical review
      // server-side, and noteAdded() unticks it here, but this payload is the state
      // the user pressed Save on. The allocation isn't in it — that's the Reallocate
      // dialog's, applied when it was made (see commonPayload).
      const payload = commonPayload();
      // A note typed into the box but never added with Add Note is saved along with
      // the incident rather than silently discarded — Save is the button people
      // reach for when they've finished with the dialog. The note goes first so the
      // PUT settles the clinical review: the other order would have the note clear a
      // review ticked in this same edit. A review that was already on the record
      // is the one the note made stale, so it must not ride back in on the PUT
      // (which would re-stamp it as reviewed by whoever pressed Save).
      return postDraftNote()
        .then(noteRes => {
          if (noteRes && "clinical_review" in payload && current?.clinical_review_at) {
            payload.clinical_review = false;
          }
          return noteRes;
        })
        .then(noteRes => axios.put(`${INCIDENTS_API}/${editing}`, payload)
          .then(() => {
            if (noteRes) notify("Note added and incident saved.", "success");
            markWroteToFollowed();  // editing it counts as adding to it
            load();
            close();
          }));
    }
  });

  const remove = () => {
    axios.delete(`${INCIDENTS_API}/${editing}`).then(() => {
      setConfirmDelete(false);
      load();
      close();
    });
  };

  // GDPR erasure: strip personal/clinical detail server-side, then refresh the
  // open dialog with the erased record so the change is visible.
  const erase = () => {
    if (typeof editing !== "number") return;
    axios.post(`${INCIDENTS_API}/${editing}/erase`).then((res) => {
      setConfirmErase(false);
      notify("Personal and clinical details erased for GDPR compliance.", "success");
      load();
      openEdit(res.data);
    });
  };

  const subtypeOptions = INCIDENT_SUBTYPES[form.detail_type] || [];
  // A stored sub-type the list no longer offers (the legacy "Pain Management" and
  // "Taping", or anything else typed in before the lists existed) is shown as it
  // is, so saving keeps it, but can't be picked again once changed away from.
  const legacySubtype = form.detail_subtype && !subtypeOptions.includes(form.detail_subtype)
    ? form.detail_subtype : null;
  // The extra incident-detail prompt fields only apply to on-course incidents.
  // (Medical details are available on every incident type.)
  const onCourse = form.location_type === "On Course";
  // Which of them actually have an answer — what the collapsed section lists.
  const recordedPrompts = PROMPT_FIELDS.filter(([key]) => (form[key] || "").trim());

  // Per-event capability gating (global roles satisfy these regardless of event).
  const evId = form.event_id ? Number(form.event_id) : undefined;
  // The open incident's own event (not necessarily the one the list is filtered
  // to), for the rules that turn on its dates.
  const formEvent = evId
    ? (availableEvents.find(e => e.id === evId) || events.find(e => e.id === evId))
    : undefined;
  // The clinical side of the incident: Medical details, the clinical review, the
  // patient's consents, and drugs given. A Race Organiser is on the event to run
  // the race, not to treat anyone, so none of it is theirs to read — the server
  // doesn't even send it (core/clinical.py), and these hide the empty shells.
  const canClinical = can("Incidents.viewClinical", { eventId: evId });
  const canScreening = can("EventParticipants.viewScreening", { eventId: evId });

  // A medical incident can't be closed without its Medical details. Judged on the
  // status *changing* to Closed, matching the backend: incidents closed before this
  // rule existed may be missing the details, and must stay editable — they're only
  // held to it if reopened and closed again. Not judged at all by someone who can't
  // see those details: their copy is blank, so the rule would read as "missing" for
  // every medical incident and block a close the server would allow (it checks the
  // stored values, not the request's).
  const closingNow = form.status === "Closed" && (isNew || initialForm.status !== "Closed");
  const otherSubtype = form.detail_subtype === OTHER_SUBTYPE;
  const medicalIncident = MEDICAL_INCIDENT_TYPES.includes(form.incident_type);
  const consentDetailsNeeded = consentNeedsDetails(form.patient_consent);
  // What a medical incident still lacks before it can close, worded for the
  // warning — the same checks as routers/incident._ensure_closable.
  const closeNeeds = !medicalIncident ? [] : [
    (!(form.detail_type || "").trim() || !(form.detail_subtype || "").trim())
      && "a Medical details type and sub-type",
    otherSubtype && !(form.detail_other || "").trim() && "a description of the Other sub-type",
    !(form.patient_consent || "").trim() && "the patient's consent",
    consentDetailsNeeded && !(form.patient_consent_details || "").trim() && "the consent details",
  ].filter(Boolean);
  const closeNeedsText = closeNeeds.length > 1
    ? `${closeNeeds.slice(0, -1).join(", ")} and ${closeNeeds[closeNeeds.length - 1]}`
    : closeNeeds[0];
  const blockClose = canClinical && closingNow && closeNeeds.length > 0;
  const consentPrompts = PATIENT_CONSENT_INFO
    .find(o => o.name === form.patient_consent)?.prompts || [];

  // The clinical fields — Medical details, Give a drug, the consents and the clinical
  // review — are only shown once there is a patient. Most of what they'd record is
  // about a person, so on an incident with nobody in it (a road closure, a lost bag)
  // they are noise on a form that is already long, and are filled in on the phone at
  // an event. Enter the patient first and they appear.
  //
  // Two escapes, both about never hiding something real. Anything already recorded
  // keeps the section on screen, whatever the patient section now says. And a close
  // blocked for want of Medical details shows them regardless — otherwise the warning
  // above would point at fields that aren't there, and the incident couldn't be
  // closed at all. Hiding is display-only: the values stay in `form` and are saved
  // back exactly as they were, so nothing is cleared by being out of sight.
  const showClinical = hasPatient || clinicalRecorded || blockClose;

  // A patient who requires follow up needs a way to be reached: a phone or an
  // email, ideally both. Prompted and highlighted, never enforced — the incident
  // still saves without, since the details may only turn up later.
  const followupContacts = [form.patient_phone, form.patient_email]
    .filter(v => `${v ?? ""}`.trim()).length;
  const followupWarn = canClinical && form.consent_followup && followupContacts < 2;
  const followupMissing = (key) => followupWarn && !`${form[key] ?? ""}`.trim();
  // An amber outline on an empty contact field, rather than `error`: red reads as
  // "can't save", and this can be.
  const warnOutline = (key) => followupMissing(key)
    ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: "warning.main" } }
    : {};
  // Whether a photo has been put anywhere on the record yet, unsaved note included,
  // so the image-consent prompt can say what's still to do rather than nag.
  const hasPhotos = [form.incident_details, newNote, ...notes.map(n => n.details)]
    .some(text => imageUrls(text).length > 0);

  // The follow-up contact prompt, shown in Patient details (where the fields are)
  // and under the Requires follow up tickbox (where the cause is). `where` words it
  // for the second, which is a long way down the form from the fields.
  const followupContactText = (where = "") => followupContacts === 0
    ? `Requires follow up — add the patient's phone or email${where} so they can be `
      + "contacted (ideally both)."
    : `Requires follow up — add the patient's `
      + `${followupMissing("patient_phone") ? "phone number" : "email"}${where} too, if you can.`;

  // The Other section's two consent prompts. Shown whether the section is expanded
  // or collapsed: each asks for something still missing, so folding the section
  // away mustn't hide it.
  const imagesPrompt = form.consent_teaching_images && !hasPhotos && (
    <Alert severity="info" sx={{ py: 0 }}>
      {isNew
        ? "Remember to add images: press Save & continue, then paste, drop "
          + "or pick photos into Incident details or a note."
        : "Remember to add images: paste, drop or pick photos into "
          + "Incident details or a note."}
    </Alert>
  );
  const followupPrompt = followupWarn && (
    <Alert severity={followupContacts === 0 ? "warning" : "info"} sx={{ py: 0, mt: 0.5 }}>
      {followupContactText(" in Patient details")}
    </Alert>
  );
  const followupContactAlert = followupWarn && (
    <Alert severity={followupContacts === 0 ? "warning" : "info"} sx={{ py: 0 }}
           action={editPatient ? undefined : (
             <Button size="small" onClick={() => setEditPatient(true)}>Add</Button>
           )}>
      {followupContactText()}
    </Alert>
  );

  // The type is required alongside the event and the summary: it is what every
  // list, filter, export and summary reads the incident by, and a blank one makes
  // the record harder to find than to fill in. It holds for an existing incident
  // as much as a new one — the same as the summary, which has always worked this
  // way — so opening one of the few that predate the rule asks for a type before
  // it will save again.
  const canSave = !!form.event_id && !!(form.incident_type || "").trim()
    && !!(form.incident_summary || "").trim() && !blockClose;

  const canEditThis = editing === "new"
    ? can("Incidents.add", { eventId: evId })
    : can("Incidents.edit", { eventId: evId });
  const canDeleteThis = can("Incidents.delete", { eventId: evId });
  const canErase = can("Incidents.erase", { eventId: evId });
  const canAllocate = can("Incidents.allocate", { eventId: evId });
  const canShare = can("Incidents.share", { eventId: evId });
  const canReview = can("Incidents.clinicalReview", { eventId: evId });
  const canStatus = can("Incidents.changeStatus", { eventId: evId });
  const canNote = can("Incidents.addNote", { eventId: evId });
  const canGiveDrug = can("Incidents.giveDrug", { eventId: evId });
  const canRaiseReport = can("Incidents.raiseClinicalReport", { eventId: evId });
  // The report types this save would raise on its own (core/clinical_report_triggers).
  const autoReports = autoReportsOnSave(form, isNew ? null : initialForm,
                                        linkedReports.map(r => r.report_type));
  const canReminder = can("Incidents.reminder", { eventId: evId });
  // Where a photo pasted or picked into the Incident details or a note is uploaded.
  // It is stored against the incident — that link is what decides who may see it
  // (core/photos.py) — so there has to be an incident first, as with notes and
  // drugs given. Null on a new one, which disables the photo controls.
  const photosUrl = typeof editing === "number" ? `${INCIDENTS_API}/${editing}/photos` : null;
  // List-level actions: can the user add to / export any event in scope?
  const canAddAny = can("Incidents.add")
    || availableEvents.some(e => can("Incidents.add", { eventId: e.id }));
  const canExport = can("Incidents.export", { eventId: eventFilter ? Number(eventFilter) : undefined });
  // Exporting every event at once is offered to a Super Admin only. Everyone else's
  // export is the view they are looking at, which is necessarily one event: the list
  // is fetched an event at a time (see load), so an all-events sheet means a second,
  // unfiltered fetch — thousands of rows with their notes — and it would in any case
  // be a partial answer for anyone whose visibility is row-level.
  const canExportAll = canExport && !!myRoles?.is_super;

  const exportFiltered = () => {
    setExportMenu(null);
    exportIncidents(filtered, selectedEvent);
  };

  // Every incident in the database, ignoring this page's filters — the event, status,
  // search and scope alike. Fetched fresh rather than assembled from what's loaded,
  // since the page only ever holds one event's worth. `allEvents` is what keeps an
  // event's name off the filename: without it the sheet would be named after whichever
  // event happens to be on the first row.
  const exportAllEvents = () => {
    setExportMenu(null);
    setExportingAll(true);
    axios.get(INCIDENTS_API + "/")
      .then(res => {
        if (res.data.length === 0) { notify("No incidents to export.", "info"); return; }
        exportIncidents(res.data, null, { allEvents: true });
      })
      .finally(() => setExportingAll(false));
  };

  // --- Post-event wrap-up exports -------------------------------------------
  // Both take a moment before anything downloads, so they share `wrapUpBusy`:
  // the photos are fetched one at a time and zipped, and the report is fetched
  // afresh from the server. Neither is offered without an event selected, since
  // both are about one event's incidents.

  // Every photograph on the incidents as listed — deliberately whatever the
  // filters have left, not "the ones with photo consent". See exportPhotos.js.
  const exportPhotos = () => {
    setWrapUpMenu(null);
    setWrapUpBusy(true);
    exportIncidentPhotos(filtered, selectedEvent)
      .then(({ saved, failed }) => {
        if (saved === 0) {
          notify(failed ? "None of the photos could be fetched." : "No photos on these incidents.",
                 failed ? "error" : "info");
        } else if (failed) {
          notify(`${saved} photo${saved === 1 ? "" : "s"} saved; ${failed} could not be fetched.`,
                 "warning");
        } else {
          notify(`${saved} photo${saved === 1 ? "" : "s"} saved.`, "success");
        }
      })
      .finally(() => setWrapUpBusy(false));
  };

  // The race organiser's copy of the incidents they had a hand in. Re-fetched
  // rather than drawn from the page's own rows, for two reasons: these come back
  // redacted to what a Race Organiser may see (the page's copies are the Super
  // Admin's unredacted ones), and the server decides which incidents qualify.
  // Chronological, which is the order the endpoint returns them in.
  const exportRaceOrganiserReport = () => {
    setWrapUpMenu(null);
    setWrapUpBusy(true);
    axios.get(`${INCIDENTS_API}/race-organiser`, { params: { event_id: eventFilter } })
      .then(res => {
        if (res.data.length === 0) {
          notify("No incidents on this event involve a Race Organiser.", "info");
          return;
        }
        const label = selectedEvent?.label_short || selectedEvent?.name || "event";
        return exportIncidentsPdf(res.data,
                                  `${fileSlug(label)}-race-organiser-incidents.pdf`,
                                  { clinical: false });
      })
      .finally(() => setWrapUpBusy(false));
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Incidents</Typography>
        <Stack direction="row" spacing={1}>
          {hasIdentity && (
            <Button variant="outlined" startIcon={<NotificationsActiveIcon />}
                    onClick={() => setRemindersOpen(true)}>
              My reminders
            </Button>
          )}
          {/* One button, two shapes: a Super Admin picks the scope from a menu, since
              they alone are offered the every-event sheet. Note it stays enabled with
              nothing listed — that only rules out the filtered option, which the menu
              disables on its own. */}
          {canExport && !canExportAll && (
            <Button variant="outlined" startIcon={<FileDownloadIcon />}
                    disabled={filtered.length === 0}
                    onClick={() => exportIncidents(filtered, selectedEvent)}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Export to Excel
            </Button>
          )}
          {canExportAll && (
            <>
              <Button variant="outlined" endIcon={<ExpandMoreIcon />}
                      startIcon={exportingAll
                        ? <CircularProgress size={18} color="inherit" />
                        : <FileDownloadIcon />}
                      disabled={exportingAll}
                      onClick={e => setExportMenu(e.currentTarget)}
                      sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                Export to Excel
              </Button>
              <Menu open={Boolean(exportMenu)} anchorEl={exportMenu} onClose={() => setExportMenu(null)}>
                <MenuItem onClick={exportFiltered} disabled={filtered.length === 0}>
                  <ListItemText primary="This view"
                                secondary={selectedEvent
                                  ? `${selectedEvent.label_short || selectedEvent.name} — ${filtered.length} as filtered`
                                  : "The listed incidents, as filtered"} />
                </MenuItem>
                <MenuItem onClick={exportAllEvents}>
                  <ListItemText primary="All incidents"
                                secondary="Every event, ignoring the filters — may take a moment" />
                </MenuItem>
              </Menu>
            </>
          )}
          {/* Post-event wrap-up, Super Admin only. Its own button rather than two
              more entries on the Excel menu: neither of these is a spreadsheet,
              and keeping them beside the wrap-up filter is what says the filter
              and the exports belong together. */}
          {canWrapUp && (
            <>
              <Button variant="outlined" endIcon={<ExpandMoreIcon />}
                      startIcon={wrapUpBusy
                        ? <CircularProgress size={18} color="inherit" />
                        : <FactCheckIcon />}
                      disabled={wrapUpBusy || !eventFilter}
                      onClick={e => setWrapUpMenu(e.currentTarget)}
                      sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                Wrap-up
              </Button>
              <Menu open={Boolean(wrapUpMenu)} anchorEl={wrapUpMenu}
                    onClose={() => setWrapUpMenu(null)}>
                <MenuItem onClick={exportPhotos} disabled={filtered.length === 0}>
                  <ListItemText primary="Photos (zip)"
                                secondary={`Every photo on the ${filtered.length} listed incident${filtered.length === 1 ? "" : "s"}, named by incident`} />
                </MenuItem>
                <MenuItem onClick={exportRaceOrganiserReport}>
                  <ListItemText primary="Race Organiser report (PDF)"
                                secondary="Incidents a Race Organiser had a hand in, as they see them — ignores the filters" />
                </MenuItem>
              </Menu>
            </>
          )}
          {canAddAny && (
            <Button variant="contained" startIcon={<AddIcon />}
                    disabled={userMode && availableEvents.length === 0} onClick={openNew}>
              Add incident
            </Button>
          )}
        </Stack>
      </Stack>

      {userMode && !hasIdentity && (
        <Alert severity="info">You are not signed in, so no incidents are available.</Alert>
      )}
      {userMode && hasIdentity && !myPerson && (
        <Alert severity="warning">
          We couldn't match your account to a Personnel record, so no events are available.
          Please ask an administrator to add you.
        </Alert>
      )}

      {/* Filters: the everyday ones in the bar, the rest behind "More filters". */}
      <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Patient name or number" size="small"
                   value={textFilter}
                   onChange={e => setTextFilter(e.target.value)} />
        <TextField label="Event" select size="small" sx={{ minWidth: 200 }}
                   value={eventFilter} onChange={onEventFilterChange}>
          {/* No "all events" option — the list is deliberately one event at a time. */}
          <MenuItem value=""><em>Select an event</em></MenuItem>
          {availableEvents.map(e => <MenuItem key={e.id} value={e.id}>{e.label_long}</MenuItem>)}
        </TextField>
        <TextField label="Status" select size="small" sx={{ minWidth: 160 }}
                   value={statusFilter} onChange={onStatusFilterChange}>
          <MenuItem value=""><em>All</em></MenuItem>
          {INCIDENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        {canClinicalListed && (
          <Tooltip title="Not yet reviewed, and of a kind that needs reviewing: a Medical Major or Minor, or anything with a Medical details type recorded.">
            <FormControlLabel
              control={<Checkbox checked={pendingReview} onChange={e => setPendingReview(e.target.checked)} />}
              label="Pending clinical review" />
          </Tooltip>
        )}
        {/* How much of what they may see to list. Each option is two lines, so the
            closed field renders its own one-line label (as the allocation picker
            does). Disabled without an identity to match on: nothing is visible then
            anyway, and the alert above says why. */}
        <TextField label="Show" select size="small" sx={{ minWidth: 210 }}
                   value={scope} disabled={!hasIdentity}
                   onChange={e => setScope(e.target.value)}
                   slotProps={{ select: { renderValue: () => scopeOption?.label || "" } }}>
          {INCIDENT_SCOPES.map(o => (
            <MenuItem key={o.value} value={o.value}>
              <Box>
                <Typography variant="body2">{o.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {o.help}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" startIcon={<FilterListIcon />}
                variant={moreActive.length ? "contained" : "outlined"}
                aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)}>
          {moreOpen ? "Fewer filters" : "More filters"}
          {moreActive.length > 0 && ` (${moreActive.length})`}
        </Button>
        <Stack direction="row" spacing={1} sx={{ ml: { sm: "auto" }, alignItems: "center" }}>
          {/* List or map. Both show the same `filtered` set, which is what keeps
              the counter beside it meaning the same thing in either. */}
          {canViewAllHere && (
            <ToggleButtonGroup exclusive size="small" value={view}
                               onChange={(_e, v) => { if (v) { setView(v); lsSet(LS_VIEW, v); } }}
                               aria-label="How to show the incidents">
              <ToggleButton value={VIEW_LIST} aria-label="List">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value={VIEW_MAP} aria-label="Map">
                <MapIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Typography variant="body2" color="text.secondary">
            {filtered.length} of {incidents.length}
          </Typography>
        </Stack>
      </Stack>

      {!moreOpen && moreActive.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          {moreActive.map(f => (
            <Chip key={f.key} size="small" label={f.label} onDelete={f.clear} />
          ))}
          <Button size="small" onClick={clearMore}>Clear all</Button>
        </Stack>
      )}

      {/* Unmounted while shut, so the pickers' option lists are only built when
          somebody opens it. */}
      <Collapse in={moreOpen} unmountOnExit>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap
                 sx={{ flexWrap: "wrap", alignItems: { sm: "center" } }}>
            <TextField label="Words in the record" size="small" sx={{ minWidth: 280 }}
                       placeholder="Summary, details, treatment, notes"
                       value={recordFilter} onChange={e => setRecordFilter(e.target.value)} />
            {/* Narrowing a whole event's caseload to one kind of incident, so it
                goes with the map behind canViewAllHere rather than being offered
                to a reader whose list is only what came to them. */}
            {canViewAllHere && (
              <TextField label="Type" select size="small" sx={{ minWidth: 220 }}
                         value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            )}
            {/* Who, from the incidents loaded, with how many each. Matched by email
                where both sides have one, else by name (incidentFilters.js). */}
            <Autocomplete size="small" sx={{ minWidth: 240 }}
                          options={creatorOpts} value={createdBy}
                          onChange={(_e, v) => setCreatedBy(v)}
                          isOptionEqualToValue={(o, v) => o.key === v.key}
                          getOptionLabel={o => o.label}
                          renderOption={(props, o) => (
                            <li {...props} key={o.key}>{o.label} ({o.count})</li>)}
                          renderInput={params => <TextField {...params} label="Created by" />} />
            <Autocomplete size="small" sx={{ minWidth: 240 }}
                          options={allocationOpts} value={allocatedTo}
                          onChange={(_e, v) => setAllocatedTo(v)}
                          isOptionEqualToValue={(o, v) => o.key === v.key}
                          getOptionLabel={o => o.label}
                          renderOption={(props, o) => (
                            <li {...props} key={o.key}>{o.label} ({o.count})</li>)}
                          renderInput={params => <TextField {...params} label="Allocated to" />} />
            {DEPLOYMENT_FIELDS.map(([key, label, options]) => (
              <TextField key={key} label={label} select size="small" sx={{ minWidth: 220 }}
                         value={deployFilter[key] || ""}
                         onChange={e => setDeployFilter(f => ({ ...f, [key]: e.target.value }))}>
                <MenuItem value=""><em>Not filtered</em></MenuItem>
                {/* With a single option "any recorded" would be the same choice
                    twice. */}
                {options.length > 1 && <MenuItem value={CHOICE_ANY}>Any recorded</MenuItem>}
                <MenuItem value={CHOICE_NONE}>None recorded</MenuItem>
                {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            ))}
            {/* Clinical fields: blank to a reader without viewClinical, so not
                offered to one. */}
            {canClinicalListed && (
              <TextField label="Medical details type" select size="small" sx={{ minWidth: 200 }}
                         value={detailTypeFilter}
                         onChange={e => { setDetailTypeFilter(e.target.value); setSubtypeFilter(""); }}>
                <MenuItem value=""><em>Not filtered</em></MenuItem>
                <MenuItem value={CHOICE_ANY}>Any recorded</MenuItem>
                <MenuItem value={CHOICE_NONE}>None recorded</MenuItem>
                {INCIDENT_DETAIL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            )}
            {canClinicalListed && (
              <TextField label="Sub-type" select size="small" sx={{ minWidth: 200 }}
                         value={subtypeFilter} disabled={!detailTypeChosen}
                         onChange={e => setSubtypeFilter(e.target.value)}>
                <MenuItem value=""><em>Not filtered</em></MenuItem>
                {subtypeOpts.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            )}
            {/* Post-event wrap-up (Super Admin). Two lines per option like "Show",
                since each needs saying what it means. Disabled until an event is
                chosen — the Race Organiser option is a question about one event's
                roster, and the others would have nothing to narrow. */}
            {canWrapUp && (
              <TextField label="Wrap-up" select size="small" sx={{ minWidth: 200 }}
                         value={wrapUp} disabled={!eventFilter}
                         onChange={e => setWrapUp(e.target.value)}
                         /* The closed field always renders text ("Not filtered"
                            when nothing is selected), so the label has to be told
                            to sit up out of the way — MUI only shrinks it on its
                            own when the value is non-empty, and "" is the
                            unfiltered state. */
                         slotProps={{ inputLabel: { shrink: true },
                                      select: { displayEmpty: true,
                                                renderValue: () => (wrapUpOption
                                                  ? wrapUpOption.label
                                                  : <Typography variant="body2" color="text.secondary">Not filtered</Typography>) } }}>
                <MenuItem value={WRAP_UP_NONE}><em>Not filtered</em></MenuItem>
                {WRAP_UP_FILTERS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box>
                      <Typography variant="body2">{o.label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {o.help}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button size="small" onClick={clearMore} disabled={moreActive.length === 0}>
              Clear these filters
            </Button>
          </Stack>
        </Paper>
      </Collapse>
      </Stack>

      {/* List */}
      {!mapView && (
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Created</TableCell>
                <TableCell>Patient</TableCell>
                {showTypeCol && <TableCell>Type</TableCell>}
                <TableCell>Summary</TableCell>
                <TableCell>Allocated to</TableCell>
                {showStatusCol && <TableCell>Status</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(i => (
                <TableRow hover key={i.id} onClick={() => openEdit(i)} sx={{ cursor: "pointer" }}>
                  <TableCell>{fmtDateTime(i.created_at)}</TableCell>
                  <TableCell>{patientLabel(i)}</TableCell>
                  {showTypeCol && <TableCell>{i.incident_type || "—"}</TableCell>}
                  <TableCell>{i.incident_summary || "—"}</TableCell>
                  <TableCell>{allocatedLabel(i) || "—"}</TableCell>
                  {showStatusCol && (
                    <TableCell>
                      {i.status && <Chip size="small" label={i.status} color={INCIDENT_STATUS_COLOR[i.status] || "default"} />}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {!eventFilter
                      ? "Select an event to view its incidents."
                      : loading ? <CircularProgress size={28} />
                      : incidents.length === 0 ? "No incidents for this event yet — add one."
                      /* The Race Organiser filter is answered by the server, so
                         an empty list means "still asking" until it replies. */
                      : (wantRaceOrganiser && raceOrganiserIds === null) ? <CircularProgress size={28} />
                      : "No incidents match your filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {/* The same rows, placed. Keyed on the event so switching one rebuilds the
          map and re-fits the view rather than leaving it over last week's hill. */}
      {mapView && (
        <Suspense fallback={
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>}>
          <IncidentMap key={eventFilter}
                       incidents={filtered}
                       locations={mapLocations}
                       locationsFailed={mapLocationsFailed}
                       routes={mapRoutes}
                       extrasLoaded={mapExtrasLoaded}
                       onOpenIncident={openEdit} />
        </Suspense>
      )}

      {/* What the rules — and the filters — are letting through. Under the list
          because it explains what's above it, and most useful when that's empty. */}
      <IncidentVisibilityNote
        eventId={eventFilter ? Number(eventFilter) : null}
        eventLabel={selectedEvent?.label_long}
        // The client-side `can()` has no notion of the grace window, so the note is
        // told separately whether this event is still in reach.
        eventInWindow={!selectedEvent || withinAccessWindow(selectedEvent)}
        userMode={userMode}
        scope={scope}
      />

      {/* Create / edit dialog */}
      <Dialog open={editing !== null} onClose={requestClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
        <DialogTitle sx={{ pb: (held || !isNew) ? 0.5 : undefined }}>
          {editing === "new" ? "Add incident" : (form.incident_summary || "Edit incident")}
          {/* The record's own id, and a link straight back to it — the same
              ?open= deep link a share texts, so it can be pasted into a message
              or a report. Only once saved: a new incident has no id yet. */}
          {!isNew && current?.id != null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                Incident ID {current.id}
              </Typography>
              <Tooltip title="Copy a link to this incident">
                <IconButton size="small" aria-label="Copy link to this incident"
                            onClick={() => {
                              navigator.clipboard?.writeText(incidentLink(current.id));
                              notify("Link copied.", "success");
                            }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {/* Says where Back goes, so following a history row doesn't feel like
              losing the record you were part way through writing. */}
          {held && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 400 }}>
              {held.editing === "new"
                ? "Opened from the incident you were adding — Back returns to it."
                : "Opened from the incident you were editing — Back returns to it."}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Basics */}
            {sectionHeading("Basics")}
            {editBasics ? (
              <>
                <TextField label="Event" select fullWidth required value={form.event_id} onChange={onEvent}>
                  {availableEvents.map(e => <MenuItem key={e.id} value={e.id}>{e.label_long}</MenuItem>)}
                </TextField>
                {/* Creator/created fields are set on save, so hide them while creating. */}
                {!isNew && (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField label="Created by" value={creatorName || "—"} sx={{ flex: 1, minWidth: 200 }}
                               InputProps={{ readOnly: true }}
                               helperText="The signed-in user who created this incident" />
                    <TextField label="Created by event role" value={creatorRole || "—"}
                               sx={{ flex: 1, minWidth: 200 }} InputProps={{ readOnly: true }}
                               helperText="Their role on the selected event" />
                    <TextField label="Created" value={fmtDateTime(current?.created_at)}
                               sx={{ flex: 1, minWidth: 200 }} InputProps={{ readOnly: true }} />
                  </Box>
                )}
                {doneRow(() => setEditBasics(false), true)}
              </>
            ) : (
              <Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      {/* On a new incident the event is the one in the form (there
                          is no saved record to read it off yet). */}
                      {(isNew
                        ? (formEvent?.label_long || (formEvent && eventLabel(formEvent)))
                        : (current?.event?.label_long || eventLabel(current?.event)))
                        || placeholder("No event")}
                    </Typography>
                    {/* Creator and created time only exist once it's saved. */}
                    {!isNew && (
                      <Typography variant="caption" color="text.secondary">
                        Created by {creatorName || "—"}{creatorRole ? ` (${creatorRole})` : ""} — {fmtDateTime(current?.created_at)}
                      </Typography>
                    )}
                  </Box>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setEditBasics(true)}>Edit</Button>
                </Box>
                {editing !== "new" && current?.id != null && (
                  <Button variant="outlined" size="small" startIcon={<HistoryIcon />}
                          onClick={() => setAuditOpen(true)} sx={{ mt: 1 }}>
                    History
                  </Button>
                )}
              </Box>
            )}

            {/* Patient details */}
            {sectionHeading("Patient details")}
            {editPatient ? (
              <>
                {participantPicker}
                {/* Whether this incident is actually linked to the entry list, which
                    is otherwise invisible once the picker has filled the fields
                    below — see linkSuggestion for how the link gets lost. */}
                {selectedParticipant ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Linked to the entry list: {participantLabel(selectedParticipant)}
                  </Typography>
                ) : linkSuggestion && (
                  <Alert severity="info" sx={{ py: 0 }}
                         action={
                           <Button size="small" onClick={() => onSelectParticipant(linkSuggestion)}>
                             Link
                           </Button>
                         }>
                    Not linked to the entry list — race number {form.patient_race_number} is{" "}
                    {participantLabel(linkSuggestion)}.
                  </Alert>
                )}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField label="Patient name" sx={{ flex: 1, minWidth: 200 }} {...field("patient_name")} />
                  <TextField label="Race number" sx={{ flex: 1, minWidth: 160 }} {...field("patient_race_number")} />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField label="Age" type="number" sx={{ width: 120 }}
                             slotProps={{ htmlInput: { min: 0, step: 1 } }} {...field("patient_age")} />
                  <TextField label="Sex" select sx={{ flex: 1, minWidth: 200 }} {...field("patient_sex")}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {PATIENT_SEXES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                  <TextField label="Race" sx={{ flex: 1, minWidth: 200 }}
                             helperText='The event race, e.g. "100 mile", "50 km"' {...field("patient_race")} />
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField label="Patient phone" sx={{ flex: 1, minWidth: 200, ...warnOutline("patient_phone") }}
                             helperText="Filled in from the participant when one is chosen"
                             {...field("patient_phone")} />
                  <TextField label="Patient email" type="email"
                             sx={{ flex: 1, minWidth: 240, ...warnOutline("patient_email") }}
                             helperText="Filled in from the participant when one is chosen"
                             {...field("patient_email")} />
                </Box>
                {followupContactAlert}
                {doneRow(() => setEditPatient(false), true)}
              </>
            ) : isNew && !hasPatient ? (
              /* A new incident, nobody chosen yet: the entry list is how the
                 patient is identified nine times out of ten, and picking one fills
                 every field behind this. The fields are a click away for the
                 casualty who isn't on it — a member of the public, or an entrant
                 the list doesn't have. */
              <>
                {participantPicker}
                <Box>
                  <Button size="small" startIcon={<EditIcon />}
                          onClick={() => setEditPatient(true)}>
                    Enter patient details by hand
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {patientSummary || placeholder("No patient details")}
                </Typography>
                {selectedParticipant && (
                  <Tooltip title="View participant details">
                    <IconButton size="small" onClick={() => setViewParticipant(selectedParticipant)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditPatient(true)}>Edit</Button>
              </Box>
            )}
            {/* The edit view shows this beside the fields themselves. */}
            {!editPatient && followupContactAlert}
            {/* Live tracking for this competitor. Outside the edit/read conditional
                above so it's there either way, and only when the event has a
                tracking link for their race and they have a race number. */}
            {trackUrl && (
              <Box>
                <Button size="small" variant="outlined" startIcon={<PersonPinCircleIcon />}
                        endIcon={<OpenInNewIcon />} onClick={() => openTracking(trackUrl)}>
                  Open tracking
                </Button>
              </Box>
            )}
            {/* The patient was flagged at pre-event screening. Shown whether or not
                the patient section is being edited, and regardless of who linked
                the participant, because it is the reason the flag exists. */}
            <WatchListAlert participant={selectedParticipant} showScreening={canScreening} />
            {/* What the patient has already had, across the whole event — the check
                before giving anything else. Drug and dose only; which batch it came
                out of is on the incident's own list of drugs given. */}
            {canClinical && patientDrugs.length > 0 && (
              <Alert severity="warning" icon={<MedicationIcon />}>
                <Typography variant="subtitle2" gutterBottom>
                  {patientDrugs.length} drug{patientDrugs.length === 1 ? "" : "s"} given
                  to this patient on this event
                </Typography>
                <Stack spacing={1}>
                  {patientDrugs.map(d => (
                    <Box key={d.id}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{drugLine(d)}</Typography>
                      {/* pre-wrap so notes written over several lines survive */}
                      {d.notes && (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{d.notes}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {fmtDateTime(d.given_at)}{d.given_by ? ` — ${d.given_by}` : ""}
                        {d.incident ? " — " : " — on this incident"}
                        {/* The incident it was given on, linked: whoever may be
                            shown the patient's history may open it. */}
                        {d.incident && (
                          <Link href={`/incidents?open=${d.incident.id}`} target="_blank" rel="noopener"
                                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <OpenInNewIcon sx={{ fontSize: 12 }} />
                            {d.incident.incident_summary || `incident #${d.incident.id}`}
                          </Link>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Alert>
            )}
            {/* Each row expands in place to the incident's key details, so reading
                what happened last time doesn't cost the reader their place in this
                one; every row also links out, so a visit already recorded can be
                added to rather than raised again. Its rows are already clinically
                redacted per reader — see components/PatientIncidentHistory.jsx. */}
            <PatientIncidentHistory incidents={otherPatientIncidents} onOpen={followHistory} />

            {/* Incident details — the type, the one-line summary, the account of
                what happened, and what was deployed. Shown in full when collapsed,
                unlike the other sections' one-line gists: this is the record
                everyone opens the incident to read, so it must never need Edit
                pressed just to see the end of it. */}
            {sectionHeading("Incident details")}
            {isNew || editIncident ? (
              <>
                {/* Required, so no blank option — as with the Event select above.
                    An incident from before the rule opens with nothing selected
                    rather than an explicit "—" to re-pick. */}
                <TextField label="Incident type" select fullWidth required
                           {...field("incident_type")}>
                  {INCIDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField label="Incident summary" required fullWidth
                           helperText="One-line summary; add detail below" {...field("incident_summary")} />
                {/* Rich text: formatting, and photos taken with the in-app camera,
                    pasted, or (not on a medical incident) picked from the device —
                    see components/RichText.jsx. */}
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}>
                    Incident details
                  </Typography>
                  <RichTextEditor value={form.incident_details} minRows={3}
                                  uploadUrl={photosUrl} uploadHint={PHOTO_HINT}
                                  allowFiles={!medicalIncident} filesHint={CLINICAL_FILES_HINT}
                                  onChange={(v) => setForm(f => ({ ...f, incident_details: v }))} />
                </Box>
                {doneRow(() => setEditIncident(false))}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {form.incident_type && <Box component="span" sx={{ color: "text.secondary" }}>{form.incident_type}: </Box>}
                    {form.incident_summary || placeholder("No summary")}
                  </Typography>
                  {/* In full, rendered as the rich text it is — remark-breaks keeps
                      the line breaks as written, including on the years of plain
                      text written before this field was rich. */}
                  <Box sx={{ mt: 1 }}>
                    {(form.incident_details || "").trim()
                      ? <RichTextView value={form.incident_details} dense />
                      : <Typography variant="body2">{placeholder("No details recorded")}</Typography>}
                  </Box>
                </Box>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditIncident(true)}>Edit</Button>
              </Box>
            )}

            {/* Location */}
            {sectionHeading("Location")}
            {editLocation ? (
              <>
                {locationPicker}
                <TextField label="Location type" select fullWidth {...field("location_type")}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {LOCATION_TYPES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </TextField>
                <TextField label="Location details" fullWidth {...field("location_details")} />
                {/* The responder's own position — the quickest way to record where
                    they are standing, and available to anyone who may edit the
                    incident. Fills all four coordinate fields below at a precision
                    matching the fix (see useLocationConvert.fromDevice). */}
                <Box>
                  <Button variant="outlined" size="small" startIcon={
                            populateBusy === "device"
                              ? <CircularProgress size={16} />
                              : <MyLocationIcon />}
                          disabled={!!populateBusy} onClick={fromDevice}>
                    {populateBusy === "device" ? "Getting your location…" : "Use my current location"}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Fills the four location fields below from this device. Your
                    browser will ask permission the first time.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField label="Grid Reference Finder link" sx={{ flex: 1 }}
                             placeholder="https://gridreferencefinder.com/os.php?gr=SD5190993629|…"
                             helperText="Paste a link, then Populate to fill all four location fields below"
                             value={grUrl}
                             onChange={e => setGrUrl(e.target.value)}
                             onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); populateFromGrUrl(); } }} />
                  <Button variant="outlined" onClick={populateFromGrUrl}
                          disabled={!!populateBusy || !grUrl.trim()} sx={{ mt: 0.5, flexShrink: 0 }}
                          startIcon={populateBusy === "link" ? <CircularProgress size={16} /> : undefined}>
                    Populate
                  </Button>
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField label="what3words" sx={{ flex: 1, minWidth: 200 }}
                             placeholder="casino.wrong.chop" {...field("location_what3words")}
                             slotProps={convertAdornment(
                               { onClick: () => fromW3w(form), enabled: !!(form.location_what3words || "").trim(),
                                 busy: populateBusy === "w3w", tip: "Fill grid ref, lat/long & postcode from this what3words address" },
                               { url: w3wUrl(form.location_what3words), enabled: !!(form.location_what3words || "").trim(),
                                 tip: "Open in what3words" })} />
                  <TextField label="Grid reference" sx={{ flex: 1, minWidth: 200 }}
                             placeholder="NY268138" {...field("location_grid_ref")}
                             slotProps={convertAdornment(
                               { onClick: () => fromGridRef(form), enabled: !!(form.location_grid_ref || "").trim(),
                                 busy: populateBusy === "gr", tip: "Fill what3words, lat/long & postcode from this grid reference" },
                               { url: gridRefUrl(form.location_grid_ref), enabled: !!(form.location_grid_ref || "").trim(),
                                 tip: "Open in Grid Reference Finder (OS mapping)" })} />
                  <TextField label="Postcode" sx={{ flex: 1, minWidth: 180 }}
                             placeholder="LA9 6JF" {...field("location_postcode")}
                             slotProps={convertAdornment(
                               { onClick: () => fromPostcode(form), enabled: !!(form.location_postcode || "").trim(),
                                 busy: populateBusy === "pc", tip: "Fill what3words, lat/long & grid reference from this postcode" },
                               { url: mapsUrl(form.location_postcode), enabled: !!(form.location_postcode || "").trim(),
                                 tip: "Open in Google Maps" })} />
                </Box>
                <TextField label="Lat / long" fullWidth
                           placeholder="54.4512, -3.0125"
                           helperText="Decimal (as copied from Google Maps) or degrees/minutes/seconds, e.g. 54°19′46″N, 002°43′55″W — converted on populate/save"
                           {...field("lat_long")}
                           slotProps={convertAdornment(
                             { onClick: () => fromLatLng(form), enabled: parseLatLng(form.lat_long)[0] != null,
                               busy: populateBusy === "ll", tip: "Fill what3words, grid reference & postcode from this lat/long" },
                             { url: mapsUrl(form.lat_long), enabled: parseLatLng(form.lat_long)[0] != null,
                               tip: "Open in Google Maps" })} />
                <Box sx={{ display: "flex" }}>
                  <Button size="small" color="inherit" startIcon={<ClearIcon />} onClick={clearLocation}>
                    Clear location fields
                  </Button>
                </Box>
                {doneRow(() => setEditLocation(false), true)}
              </>
            ) : isNew && !hasLocation ? (
              /* A new incident with no location yet: the event's own list, or the
                 course. Everything else — the four coordinate systems, the device
                 fix, the Grid Reference Finder paste — is behind these two, since a
                 location that is on the list needs none of it. On Course is also
                 the way to a location that is neither, the type being just another
                 field once the fields are open. */
              <>
                {locationPicker}
                <Box>
                  <Button variant="outlined" size="small" startIcon={<PlaceIcon />}
                          onClick={startOnCourseLocation}>
                    On course location
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    {[form.location_type, form.location_details].filter(Boolean).join("  ·  ") ||
                      placeholder("No location set")}
                  </Typography>
                  <Box>
                    {locValue("what3words", form.location_what3words,
                              (form.location_what3words || "").trim() ? w3wUrl(form.location_what3words) : null)}
                    {locValue("Grid ref", form.location_grid_ref,
                              (form.location_grid_ref || "").trim() ? gridRefUrl(form.location_grid_ref) : null)}
                    {locValue("Postcode", form.location_postcode,
                              (form.location_postcode || "").trim() ? mapsUrl(form.location_postcode) : null)}
                    {locValue("Lat/long", form.lat_long,
                              parseLatLng(form.lat_long)[0] != null ? mapsUrl(form.lat_long) : null)}
                  </Box>
                </Box>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditLocation(true)}>Edit</Button>
              </Box>
            )}

            {/* Phone Finder — text the casualty a link to share their phone GPS.
                Only on a saved incident (needs an id) and with edit permission.
                The action (button/helper/refresh) is offered only when the
                location still needs finding — location type blank or On Course —
                but any results already received stay visible regardless. */}
            {editing !== "new" && can("Incidents.phoneFind", { eventId: evId }) && (
              <Box sx={{ pl: 0.5 }}>
                {(!form.location_type || form.location_type === "On Course") && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Button size="small" variant="outlined" startIcon={<PhoneIphoneIcon />}
                            onClick={openPhoneFind}>
                      Phone Finder
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Texts the casualty a link to share their phone's GPS location.
                    </Typography>
                  </Stack>
                )}

                {phoneFindLink && (
                  <Alert severity="info" sx={{ mt: 1 }}
                         action={
                           <Button color="inherit" size="small" startIcon={<ContentCopyIcon />}
                                   onClick={() => { navigator.clipboard?.writeText(phoneFindLink); notify("Link copied.", "success"); }}>
                             Copy
                           </Button>}>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>{phoneFindLink}</Typography>
                  </Alert>
                )}

                {phoneFinds.map(pf => {
                  const s = PHONE_FIND_STATUS[pf.status] || PHONE_FIND_STATUS.pending;
                  const ll = (pf.latitude != null && pf.longitude != null)
                    ? `${pf.latitude.toFixed(5)}, ${pf.longitude.toFixed(5)}` : null;
                  return (
                    <Box key={pf.id} sx={{ mt: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                        <Chip size="small" color={s.color} label={s.label} />
                        <Typography variant="caption" color="text.secondary">
                          {pf.phone ? `${pf.phone} · ` : ""}sent {fmtDateTime(pf.created_at)}
                        </Typography>
                      </Stack>
                      {pf.status === "located" && (
                        <Box sx={{ mt: 0.5 }}>
                          {locValue("Grid ref", pf.grid_ref, pf.grid_ref ? gridRefUrl(pf.grid_ref) : null)}
                          {locValue("Lat/long", ll, ll ? mapsUrl(ll) : null)}
                          <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                            <Box component="span" sx={{ color: "text.secondary" }}>Accuracy:</Box>{" "}
                            +/- {pf.accuracy_m != null ? Math.round(pf.accuracy_m) : "?"} m
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            Received {fmtDateTime(pf.located_at)}
                          </Typography>
                          <Button size="small" startIcon={<MyLocationIcon />} sx={{ mt: 0.5 }}
                                  onClick={() => applyPhoneFindLocation(pf)}>
                            Use as incident location
                          </Button>
                        </Box>
                      )}
                      {pf.status === "error" && pf.error_message && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {pf.error_message}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {(!form.location_type || form.location_type === "On Course") && (
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => refreshPhoneFinds(editing)}>Refresh results</Button>
                  </Box>
                )}
              </Box>
            )}

            {/* On course details — the prompted questions that only apply
                to an incident out on the course. Any value already recorded is
                shown whatever the location now says, so changing the location
                doesn't hide what was written. */}
            {sectionHeading("On course details")}
            {isNew || editOnCourse ? (
              <>
                {onCourse ? (
                  PROMPT_FIELDS.map(([key, label]) => (
                    <Fragment key={key}>
                      <TextField label={label} fullWidth multiline minRows={2}
                                 helperText={INCIDENT_DETAIL_PROMPTS[key]} {...field(key)} />
                      {/* Shortcut for filling in the group the casualty is with:
                          it appends the entrant's details to the box above and
                          stores no link to them (see addToWithWhom). Sits under
                          that field rather than beside it so it reads as a way of
                          writing into it, and the text stays fully editable. */}
                      {key === "with_whom" && (
                        <Autocomplete
                          options={participants}
                          value={null}
                          inputValue={withWhomSearch}
                          onInputChange={(_, v) => setWithWhomSearch(v)}
                          onChange={(_, p) => { addToWithWhom(p); setWithWhomSearch(""); }}
                          getOptionLabel={participantLabel}
                          blurOnSelect
                          disabled={!form.event_id}
                          noOptionsText={form.event_id ? "No participants — import an entry list on the event" : "Select an event first"}
                          renderInput={(params) => (
                            <TextField {...params} size="small" label="Add an entrant to With whom"
                                       helperText="Search the entry list by name or race number — adds their name, number and phone to the box above" />
                          )}
                        />
                      )}
                    </Fragment>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    These fields are hidden unless Location is On Course
                  </Typography>
                )}
                {doneRow(() => setEditOnCourse(false))}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  {recordedPrompts.length === 0 ? (
                    <Typography variant="body2">
                      {placeholder(onCourse
                        ? "Nothing recorded yet"
                        : "Only recorded for On Course incidents")}
                    </Typography>
                  ) : recordedPrompts.map(([key, label]) => (
                    /* Label above the value, not inline: these answers run to
                       paragraphs, and they're shown in full. */
                    <Box key={key} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: "block", fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {form[key]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOnCourse(true)}>Edit</Button>
              </Box>
            )}

            {/* Medical details — the whole section is absent for a reader without
                the clinical gate; the server sends none of it either. */}
            {canClinical && sectionHeading("Medical details")}
            {canClinical && blockClose && (
              <Alert severity="warning">
                To close this {form.incident_type} incident, record {closeNeedsText} below.
              </Alert>
            )}
            {/* Expanded while blocking a close, so the fields needed are to hand. */}
            {!canClinical ? null : !showClinical ? (
              <Typography variant="body2" color="text.secondary">
                These fields are hidden until a patient is entered above
              </Typography>
            ) : isNew || editTreatment || blockClose ? (
              <>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* One type per incident, so a patient treated for more than one
                      thing needs the reader told which to record. */}
                  {/* Each type's option says what it is for, so the closed field
                      renders just the name. */}
                  <TextField label="Type" select sx={{ flex: 1, minWidth: 200 }}
                             helperText="For multiple treatments pick the most significant."
                             value={form.detail_type} onChange={onDetailType}
                             slotProps={{ select: {
                               renderValue: (v) => v,
                               MenuProps: { slotProps: { paper: { sx: { maxWidth: 480 } } } },
                             } }}>
                    <MenuItem value=""><em>—</em></MenuItem>
                    {INCIDENT_DETAIL_TYPE_INFO.map(t => (
                      <MenuItem key={t.name} value={t.name}>
                        <Box>
                          <Typography variant="body2">{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary"
                                      sx={{ display: "block", whiteSpace: "normal" }}>
                            {t.use}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* Type "Other" has only the one sub-type, set for it, so there is
                      nothing to choose. */}
                  {form.detail_type !== "Other" && (
                    <TextField label="Sub-type" select sx={{ flex: 1, minWidth: 240 }}
                               disabled={!form.detail_type}
                               helperText={legacySubtype
                                 ? "No longer offered — kept as recorded, until changed."
                                 : form.detail_type ? " " : "Choose a type first"}
                               {...field("detail_subtype")}>
                      <MenuItem value=""><em>—</em></MenuItem>
                      {legacySubtype && (
                        <MenuItem value={legacySubtype} disabled>
                          {legacySubtype} (no longer offered)
                        </MenuItem>
                      )}
                      {subtypeOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  )}
                  {otherSubtype && (
                    <TextField label={form.detail_type === "Other" ? "Describe" : "Describe the Other"}
                               sx={{ flex: 1, minWidth: 240 }}
                               {...field("detail_other")} />
                  )}
                </Box>
                {/* Consent, before the treatment it was given (or withheld) for.
                    Marked required on a medical incident, which can't close without
                    it — but it never blocks an ordinary save. */}
                <TextField label="Patient consent" select fullWidth required={medicalIncident}
                           error={blockClose && !(form.patient_consent || "").trim()}
                           {...field("patient_consent")}>
                  <MenuItem value=""><em>Not recorded</em></MenuItem>
                  {PATIENT_CONSENT_INFO.map(o => (
                    <MenuItem key={o.name} value={o.name} sx={{ whiteSpace: "normal" }}>{o.name}</MenuItem>
                  ))}
                  {/* An off-list value stays selectable, so opening the record
                      doesn't edit it. */}
                  {form.patient_consent && !PATIENT_CONSENT_INFO.some(o => o.name === form.patient_consent) && (
                    <MenuItem value={form.patient_consent}>{form.patient_consent} (no longer offered)</MenuItem>
                  )}
                </TextField>
                {consentDetailsNeeded && (
                  <TextField label="Consent details" fullWidth multiline minRows={4}
                             required={medicalIncident}
                             error={blockClose && !(form.patient_consent_details || "").trim()}
                             // The prompts are a list, which can't sit inside the
                             // helper text's default <p>.
                             slotProps={{ formHelperText: { component: "div" } }}
                             helperText={consentPrompts.length > 0 && (
                               <>
                                 Include:
                                 <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                   {consentPrompts.map(t => <li key={t}>{t}</li>)}
                                 </Box>
                               </>
                             )}
                             {...field("patient_consent_details")} />
                )}
                {childPatient && (
                  <TextField label="Child consent given by" fullWidth
                             helperText="The patient is under 16: who gave consent for them, and their relationship to the patient (e.g. parent, guardian)."
                             {...field("patient_consent_child_by")} />
                )}
                <TextField label="Treatment given" fullWidth multiline minRows={3}
                           helperText="Treatments, drugs with dosages, advice, etc."
                           {...field("treatment_given")} />
                {/* Safety netting: each category of advice the patient was given,
                    recorded as a chip. Zero or one is the usual answer, so the
                    options are behind one button and the notes saying what each
                    covers travel with them onto the menu — they belong in front of
                    whoever is choosing, which a bare multi-select couldn't do. */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Safety netting
                  </Typography>
                  {nettingChosen.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                      {nettingChosen.map(n => (
                        /* Keyed on the option, not the stored value, so typing in
                           the Other box below doesn't remount its chip. */
                        <Chip key={isOtherNetting(n) ? SAFETY_NETTING_OTHER : n} size="small"
                              label={isOtherNetting(n) ? SAFETY_NETTING_OTHER : n}
                              onDelete={() => (isOtherNetting(n)
                                ? setOtherNetting(null) : toggleSafetyNetting(n))} />
                      ))}
                    </Box>
                  )}
                  {(nettingLeft.length > 0 || !otherNettingOn) && (
                    <Box>
                      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5 }}
                              onClick={e => setNettingMenu(e.currentTarget)}>
                        {nettingChosen.length ? "Add another" : "Add safety netting"}
                      </Button>
                    </Box>
                  )}
                  <Menu anchorEl={nettingMenu} open={!!nettingMenu}
                        onClose={() => setNettingMenu(null)}
                        slotProps={{ paper: { sx: { maxWidth: 380 } } }}>
                    {nettingLeft.map(o => (
                      <MenuItem key={o.name} sx={{ whiteSpace: "normal" }}
                                onClick={() => { toggleSafetyNetting(o.name); setNettingMenu(null); }}>
                        <Box>
                          <Typography variant="body2">{o.name}</Typography>
                          {o.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {o.notes}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {/* Other, last: the advice itself is typed into the box below,
                        which appears with the chip. */}
                    {!otherNettingOn && (
                      <MenuItem sx={{ whiteSpace: "normal" }}
                                onClick={() => { setOtherNetting(""); setNettingMenu(null); }}>
                        <Box>
                          <Typography variant="body2">{SAFETY_NETTING_OTHER}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            anything else the patient was told to look out for
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                  </Menu>
                  {otherNettingOn && (
                    <TextField label="Other safety netting advice" fullWidth multiline
                               sx={{ mt: 1 }} value={otherNetting}
                               onChange={e => setOtherNetting(e.target.value)} />
                  )}
                </Box>
                <TextField label="Advised to retire" select sx={{ maxWidth: 360 }}
                           {...field("advised_to_retire")}>
                  <MenuItem value=""><em>Not advised</em></MenuItem>
                  {ADVISED_TO_RETIRE_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  {/* A value that isn't an option — the legacy "Yes" from when this
                      was a tickbox — stays selectable until an outcome is picked,
                      so opening the record doesn't edit it. */}
                  {form.advised_to_retire && !ADVISED_TO_RETIRE_OPTIONS.includes(form.advised_to_retire) && (
                    <MenuItem value={form.advised_to_retire}>
                      {form.advised_to_retire} (outcome not recorded)
                    </MenuItem>
                  )}
                </TextField>
                {doneRow(() => setEditTreatment(false))}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {[form.detail_type, detailSubtypeLabel(form)].filter(Boolean).join("  ·  ") || placeholder("No type set")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Consent: {form.patient_consent || placeholder("Not recorded")}
                  </Typography>
                  {consentDetailsNeeded && (form.patient_consent_details || "").trim() && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                      {form.patient_consent_details}
                    </Typography>
                  )}
                  {childPatient && (
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}>
                      Child consent given by: {(form.patient_consent_child_by || "").trim() || "not recorded"}
                    </Typography>
                  )}
                  {/* In full, and pre-wrap so a dose written over several lines
                      survives — as with Incident details above. */}
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                    {form.treatment_given || placeholder("No treatment recorded")}
                  </Typography>
                  {/* Names only: the notes belong beside the checkboxes, where the
                      advice is being chosen, not in the summary of what was. */}
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}>
                    Safety netting: {(form.safety_netting || []).join("  ·  ") || "none recorded"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}>
                    Advised to retire: {form.advised_to_retire || "No"}
                  </Typography>
                </Box>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditTreatment(true)}>Edit</Button>
              </Box>
            )}

            {/* Drugs given from stock. Written against the incident, so it needs
                the incident to exist first — same as the Notes section below. Goes
                with the rest of the clinical fields: a drug is given to somebody. */}
            {showClinical && (canGiveDrug || drugs.length > 0) && (
              <Box>
                {drugs.length > 0 && (
                  <DrugAdministrationList drugs={drugs} incidentId={editing}
                                          canEdit={canGiveDrug} onChanged={noteAdded} />
                )}
                {canGiveDrug && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<MedicationIcon />}
                            sx={{ mt: drugs.length ? 1 : 0 }}
                            disabled={isNew} onClick={() => setDrugOpen(true)}>
                      Give a drug
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {isNew
                        ? "A drug given is recorded against the incident, so press "
                          + "Save & continue below to file it first — the dialog stays open."
                        : "Records the drug, the batch it came from and the dose, and takes it out of stock."}
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {/* Raising a clinical report about this incident. Against the incident
                like a drug, so it needs the incident filed first; prefilled from
                it as saved. */}
            {showClinical && canRaiseReport && (
              <Box>
                <Button size="small" variant="outlined" startIcon={<ReportProblemIcon />}
                        disabled={isNew} onClick={() => setReportOpen(true)}>
                  Raise a clinical report
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {isNew
                    ? "A report is raised from the incident, so press "
                      + "Save & continue below to file it first — the dialog stays open."
                    : JSON.stringify(form) !== JSON.stringify(initialForm)
                      ? "A report is prefilled from the incident as last saved — save your changes first to include them."
                      : "For a refusal, a medication error, a safeguarding concern or similar — goes to the Compliance Manager."}
                </Typography>
              </Box>
            )}

            {/* What saving will raise on its own — the server's rule, mirrored in
                clinicalReportRules.autoReportsOnSave — so nobody raises the same
                report by hand as well. */}
            {canClinical && autoReports.length > 0 && (
              <Alert severity="info" sx={{ py: 0 }}>
                Saving will automatically raise {autoReports.length > 1 ? "clinical reports" : "a clinical report"}:{" "}
                {autoReports.join("; ")}.
              </Alert>
            )}

            {/* The clinical reports naming this incident, raised by hand or
                automatically, listed to whoever sees the medical details. Opening
                one is narrower — the Compliance Manager, the event's Clinical
                Lead/Deputy and whoever raised it (core/clinical_reports.may_read) —
                so the server says per row, and only those are links. Shown
                whatever showClinical says: something real is never hidden. */}
            {linkedReports.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Clinical reports about this incident
                </Typography>
                {linkedReports.map(r => (
                  <Box key={r.id} sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                    {r.can_open ? (
                      <Link component="button" type="button" variant="body2"
                            onClick={() => setOpenReportId(r.id)}>
                        {r.reference}
                      </Link>
                    ) : (
                      <Typography variant="body2">{r.reference}</Typography>
                    )}
                    <Typography variant="body2">
                      {r.report_type === "Other" && r.type_other ? r.type_other : r.report_type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.status}{r.harm ? ` · harm: ${r.harm}` : ""} · raised {fmtDateTime(r.created_at)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Notes */}
            {sectionHeading("Notes")}
            {notes.length > 0 && <Typography variant="subtitle2">Notes ({notes.length})</Typography>}
            {notes.map(n => (
              <Box key={n.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                {(n.details || "").trim()
                  ? <RichTextView value={n.details} dense />
                  : <Typography variant="body2">—</Typography>}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Added by {n.added_by || "—"}{n.added_by_role ? ` (${n.added_by_role})` : ""} — {fmtDateTime(n.added_at)}
                  </Typography>
                  {((me.email && (n.added_by_email || "").toLowerCase() === me.email.toLowerCase())
                    || can("Incidents.edit", { eventId: evId })) && (
                    <Tooltip title="Delete note">
                      <IconButton size="small" color="error" onClick={() => deleteNote(n.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
            {editing === "new" ? (
              <Typography variant="body2" color="text.secondary">
                Press Save &amp; continue below to file the incident, then add notes — the
                dialog stays open.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: "block", mb: 0.5 }}>
                    New note
                  </Typography>
                  <RichTextEditor value={newNote} onChange={setNewNote} minRows={2}
                                  uploadUrl={photosUrl} uploadHint={PHOTO_HINT}
                                  allowFiles={!medicalIncident} filesHint={CLINICAL_FILES_HINT} />
                </Box>
                <Button variant="outlined" sx={{ mt: { sm: 3 }, whiteSpace: "nowrap" }}
                        startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
                        disabled={!newNote.trim() || !canNote || saving} onClick={addNote}>
                  Add Note
                </Button>
              </Box>
            )}

            {/* Allocation */}
            {sectionHeading("Allocation")}
            {/* A new incident's allocation is part of creating it — picked here,
                applied by Save, and nobody is texted. An existing one is only ever
                changed through the Reallocate dialog below, which does it at once
                and offers the text; there is no half-changed state to explain. */}
            {isNew ? (
              <TextField label="Allocated to" select sx={{ maxWidth: 360 }} fullWidth
                         value={allocValue} onChange={onAlloc} disabled={!canAllocate}
                         // A team's option is two lines (name + who's in it), so the
                         // closed field renders its own one-line summary instead.
                         slotProps={{ select: { renderValue: () => allocName || "—" } }}
                         helperText={allocTeam
                           ? teamMemberNames(allocTeam)
                           : (form.event_id ? " " : "Select an event first")}>
                <MenuItem value="creator">{creatorName ? `${creatorName} (creator)` : "Creator"}</MenuItem>
                {/* Teams first: allocating to one makes the incident everyone
                    in it's, which is usually the broader choice. */}
                {teams.length > 0 && <ListSubheader>Teams</ListSubheader>}
                {teams.map(t => (
                  <MenuItem key={`t:${t.id}`} value={`t:${t.id}`}>
                    <Box>
                      <Typography variant="body2">{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: "block" }}>
                        {teamMemberNames(t)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                {allocGrouped && allocReachable.length > 0 && <ListSubheader>People</ListSubheader>}
                {allocReachable.map(allocItem)}
                {/* Allocating to them is fine; texting them isn't. Said over the
                    group rather than under each name — with a dozen Mountain Safety
                    attendees the repeated caption was most of the list. */}
                {allocUnreachable.length > 0 && (
                  <ListSubheader sx={{ whiteSpace: "normal", lineHeight: 1.5, py: 1 }}>
                    These can't open incidents — won't be texted
                  </ListSubheader>
                )}
                {allocUnreachable.map(allocItem)}
              </TextField>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {allocName
                      ? `${allocName}${allocRole ? ` (${allocRole})` : ""}`
                      : placeholder("Allocated to creator")}
                  </Typography>
                  {allocTeam && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {teamMemberNames(allocTeam)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Updated {fmtDateTime(current?.allocated_to_updated_at)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Reallocate and Share, side by side because they are the same
                decision one step apart: hand it over, or ask someone to look while
                it stays where it is. Both write a note, so both need the incident
                to exist; both offer the text and neither depends on it. */}
            {!isNew && (canAllocate || canShare) && (
              <Box>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {canAllocate && (
                    <Button size="small" variant="outlined" startIcon={<AssignmentIndIcon />}
                            onClick={() => setTargetAction("allocate")}>
                      Reallocate
                    </Button>
                  )}
                  {canShare && (
                    <Button size="small" variant="outlined" startIcon={<ShareIcon />}
                            onClick={() => setTargetAction("share")}>
                      Share
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {canAllocate && canShare
                    ? "Reallocating hands the incident over; sharing lets someone open "
                      + "it without changing who holds it. Either can text them a link."
                    : canAllocate
                      ? "Hands the incident to someone else on the event, or one of its teams."
                      : "Lets someone on the event open this incident without changing "
                        + "who holds it."}
                </Typography>
              </Box>
            )}

            {/* Other */}
            {sectionHeading("Other")}
            {/* The consents were asked and recorded as part of the patient's care,
                so they go with the rest of the clinical record — including being
                hidden until there is a patient to have consented. */}
            {canClinical && !showClinical && (
              <Typography variant="body2" color="text.secondary">
                Consent and clinical review are hidden until a patient is entered
              </Typography>
            )}
            {/* Everything down to Status collapses on a saved incident, like the
                sections above — set on few incidents and rarely changed. Status and
                the clinical review below stay out, since they're what's changed
                most often once an incident exists. */}
            {isNew || editOther ? (
              <>
                {canClinical && showClinical && (
                  <Box>
                    <FormControlLabel
                      control={<Checkbox checked={form.consent_teaching_images}
                                         onChange={e => setForm({ ...form, consent_teaching_images: e.target.checked })} />}
                      label="Patient consents to sharing anonymous images for teaching?" />
                    {/* Consent is only worth having if there are images to go with it,
                        and they are taken in the moment — so ask for them while the
                        patient is still here. Quiet once there is a photo. */}
                    {imagesPrompt}
                  </Box>
                )}
                {canClinical && showClinical && (
                  <Box>
                    <FormControlLabel
                      control={<Checkbox checked={form.consent_followup}
                                         onChange={e => setForm({ ...form, consent_followup: e.target.checked })} />}
                      label="Requires follow up" />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                      For when we want to follow the patient up after the event, and they
                      have consented to that.
                    </Typography>
                    {followupPrompt}
                  </Box>
                )}
                {/* Deployments and disposal — any incident, on course or not, but
                    set on few, hence down here (see DEPLOYMENT_FIELDS). */}
                {/* In pairs — the two deployments, then the two disposal fields — so
                    each row reads as one question rather than wrapping wherever the
                    width runs out. */}
                {[DEPLOYMENT_FIELDS.slice(0, 2), DEPLOYMENT_FIELDS.slice(2)].map(pair => (
                  <Box key={pair[0][0]} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {pair.map(([key, label, options]) => (
                      <TextField key={key} label={label} select sx={{ flex: 1, minWidth: 200 }}
                                 {...field(key)}>
                        <MenuItem value=""><em>—</em></MenuItem>
                        {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </TextField>
                    ))}
                  </Box>
                ))}
                {doneRow(() => setEditOther(false))}
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  {canClinical && showClinical && (
                    <Typography variant="body2">
                      Consents to teaching images: {form.consent_teaching_images ? "Yes" : "No"}
                      {"  ·  "}
                      Requires follow up: {form.consent_followup ? "Yes" : "No"}
                    </Typography>
                  )}
                  {imagesPrompt}
                  {followupPrompt}
                  {/* Only what was recorded; a blank says nothing, as in the PDF. */}
                  <Typography variant="body2" sx={{ mt: canClinical && showClinical ? 0.5 : 0 }}>
                    {DEPLOYMENT_FIELDS.filter(([key]) => form[key])
                      .map(([key, label]) => `${label}: ${form[key]}`).join("  ·  ")
                      || placeholder("No deployment or disposal recorded")}
                  </Typography>
                </Box>
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOther(true)}>Edit</Button>
              </Box>
            )}
            <TextField label="Status" select sx={{ maxWidth: 240 }} disabled={!canStatus}
                       error={blockClose} {...field("status")}
                       helperText={blockClose
                         ? `A ${form.incident_type} incident needs ${closeNeedsText} before it can be closed.`
                         : undefined}>
              {INCIDENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            {canClinical && showClinical && (
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={form.clinical_review} disabled={!canReview}
                                     onChange={e => setForm({ ...form, clinical_review: e.target.checked })} />}
                  label="Clinical review" />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                  {form.clinical_review
                    ? (current?.clinical_review_at
                        ? `Reviewed by ${current.clinical_review_by || "—"} on ${fmtDateTime(current.clinical_review_at)}`
                        : `Will be recorded as reviewed by ${me.name || "you"} on save`)
                    : "Not yet clinically reviewed"}
                </Typography>
              </Box>
            )}

            {/* Reminder — set/edit a personal SMS review reminder for this incident */}
            {editing !== "new" && canReminder && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Reminder</Typography>
                {incidentReminder ? (
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <NotificationsActiveIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {incidentReminder.active
                        ? `Next reminder ${fmtDateTime(incidentReminder.next_fire_at)}`
                        : "Reminder finished"}
                      {" — "}
                      {incidentReminder.recurring
                        ? `every ${incidentReminder.interval_hours}h, ${incidentReminder.occurrences_sent}/${incidentReminder.occurrences_total} sent`
                        : "one-off"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setReminderFormOpen(true)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />}
                            onClick={() => setConfirmDeleteReminder(true)}>Delete</Button>
                  </Stack>
                ) : (
                  <Button size="small" startIcon={<NotificationsActiveIcon />} onClick={() => setReminderFormOpen(true)}>
                    Set a reminder
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Reminds you by SMS to review this incident. Only you receive your reminder.
                </Typography>
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              {editing !== "new" && canDeleteThis && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )}
              {editing !== "new" && canErase && (
                <Button color="warning" startIcon={<AutoDeleteIcon />} onClick={() => setConfirmErase(true)}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                  Erase
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {editing !== "new" && can("Incidents.export", { eventId: evId }) && (
                <Button startIcon={<FileDownloadIcon />}
                        onClick={() => exportIncidentPdf({ ...current, notes },
                                                         { clinical: canClinical })}>
                  Export PDF
                </Button>
              )}
              {/* "Cancel" only while nothing has been filed. Once the record
                  exists this button abandons the edits in front of you, not the
                  incident — which is still there either way. */}
              <Button onClick={requestClose}>
                {held ? "Back" : isNew ? "Cancel" : "Close"}
              </Button>
              {/* Filing the incident without leaving it, so the things that can
                  only be written against a saved record — a drug given, a note, a
                  photo — are reachable while it's still being written up. */}
              {isNew && (
                <Tooltip title="File this incident and carry on editing it, so you can add notes, photos and drugs given">
                  <span>
                    <Button disabled={!canSave || !canEditThis || saving}
                            startIcon={saving ? <CircularProgress size={16} /> : undefined}
                            onClick={saveAndContinue}>
                      {saving ? "Saving…" : "Save & continue"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button variant="contained" disabled={!canSave || !canEditThis || saving}
                      startIcon={saving ? <CircularProgress size={16} /> : undefined}
                      onClick={save}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Set / edit the current user's reminder for the open incident */}
      <ReminderFormDialog
        open={reminderFormOpen}
        onClose={() => setReminderFormOpen(false)}
        onSaved={() => { if (typeof editing === "number") refreshIncidentReminder(editing); }}
        incident={current}
        reminder={incidentReminder}
        me={{ name: myName, email: myEmail }}
      />

      {/* Reminder delete confirmation */}
      <Dialog open={confirmDeleteReminder} onClose={() => setConfirmDeleteReminder(false)}>
        <DialogTitle>Delete reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reminder will be removed and will not send again. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteReminder(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={deleteIncidentReminder}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record a drug given to the patient (and take it out of stock) */}
      <GiveDrugDialog
        open={drugOpen}
        onClose={() => setDrugOpen(false)}
        onSaved={noteAdded}
        incidentId={typeof editing === "number" ? editing : null}
        deductsStock={eventDeductsStock(formEvent)}
      />

      {/* Raise a clinical report about the incident */}
      <RaiseClinicalReportDialog
        open={reportOpen}
        onClose={() => { setReportOpen(false); setReportsKey(k => k + 1); }}
        incidentId={typeof editing === "number" ? editing : null}
      />

      {/* A report named above, opened over the incident so closing it comes back
          here. The dialog fetches the report itself and gates its own edits. */}
      <ClinicalReportDialog
        reportId={openReportId}
        onClose={() => { setOpenReportId(null); setReportsKey(k => k + 1); }}
        onSaved={() => setReportsKey(k => k + 1)}
        onDeleted={() => { setOpenReportId(null); setReportsKey(k => k + 1); }}
      />

      {/* Reallocate / Share — one dialog for both, told apart by `action`. Each
          applies through its own call (see reallocate / shareIncident), which is
          also what refreshes the notes: both write one. Not noteAdded(), because
          neither note invalidates a clinical review the way a clinical one does,
          and the server doesn't clear it for either. */}
      <IncidentTargetDialog
        open={!!targetAction}
        action={targetAction || "share"}
        onClose={() => setTargetAction(null)}
        incidentId={typeof editing === "number" ? editing : null}
        attendances={attendances}
        teams={teams}
        creator={{ name: creatorName }}
        currentValue={allocValue}
        onSubmit={targetAction === "allocate" ? reallocate : shareIncident}
      />

      {/* "My reminders" list */}
      <RemindersDialog
        open={remindersOpen}
        onClose={() => setRemindersOpen(false)}
        me={{ name: myName, email: myEmail }}
        onOpenIncident={openIncidentById}
      />

      {/* Phone Finder — enter the casualty's number and text them the locate link */}
      <Dialog open={phoneFindOpen} onClose={() => !phoneFindBusy && setPhoneFindOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Phone Finder</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Text the casualty a link that asks them to share their phone's GPS
            location. The link is valid for a limited time and requires no login.
          </DialogContentText>
          <TextField
            autoFocus fullWidth label="Casualty mobile number"
            value={phoneFindPhone}
            onChange={e => setPhoneFindPhone(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !phoneFindBusy) { e.preventDefault(); sendPhoneFind(); } }}
            helperText="UK mobile (07…) or international (+44…, or another country code). An SMS is sent from Event Control."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneFindOpen(false)} disabled={phoneFindBusy}>Cancel</Button>
          <Button variant="contained" onClick={sendPhoneFind} disabled={phoneFindBusy}
                  startIcon={phoneFindBusy ? <CircularProgress size={16} /> : <PhoneIphoneIcon />}>
            Send link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Incident audit trail (history) */}
      <AuditTrail
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        targetType="incident"
        targetId={current?.id}
        title={`History — ${current?.incident_summary || "Incident"}`}
      />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete incident?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this incident. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={remove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* GDPR erasure confirmation */}
      <Dialog open={confirmErase} onClose={() => setConfirmErase(false)}>
        <DialogTitle>Erase personal data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action erases personal and clinical details from an Incident to comply
            with a GDPR data erasure request, while retaining basic information for
            statistical purposes. Please confirm you wish to continue. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmErase(false)}>Cancel</Button>
          <Button color="warning" variant="contained" startIcon={<AutoDeleteIcon />} onClick={erase}>
            Erase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this incident. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={close}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coming back from a followed patient-history row, having added to it, to a
          new incident that was never saved. Asked rather than assumed: the visit is
          on the record now, so the half-written one is probably the duplicate this
          link exists to avoid — but a patient can have two problems, and the reader
          is the only one who knows. Nothing has been saved either way, so both
          answers are safe; the wording says what each does. */}
      <Dialog open={confirmKeepHeld} onClose={() => setConfirmKeepHeld(false)}>
        <DialogTitle>Still need the new incident?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You added to an incident that was already recorded for this patient. The
            new incident you started hasn't been saved — carry on with it, or drop it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={discardHeld}>Drop it</Button>
          <Button variant="contained" onClick={returnToHeld}>
            Carry on with it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only participant details (incl. fields not on the incident form) */}
      <ParticipantDetailsDialog participant={viewParticipant} showScreening={canScreening}
                                onClose={() => setViewParticipant(null)} />
    </Stack>
  );
}
